import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import PhoneCopyEnhancer from '../PhoneCopyEnhancer';
import BmrPreflightHost from '../BmrPreflightHost';
import { useResponsive } from '../../hooks/useResponsive';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import useScreenshotPrevention from '../../hooks/useScreenshotPrevention';
import MobileBottomNav from './MobileBottomNav';
import { NAV_ICONS as I, ICON_MAP } from '../../config/navIcons';
import MobileHeader from './MobileHeader';
import { NAV_CATALOG } from '../../config/navCatalog';
import { canSeeAeris } from '../../config/ecosystemApps';
import { AERIS_OPEN_PATH } from '../../utils/aerisLink';
import { useFioriUi } from '../../config/uiMode';
import FioriShellBar from './FioriShellBar';
import BarcodeScanner from '../BarcodeScanner';
import { T as DS } from '../../design-system/tokens'; // design tokens (theme var below is named T)
import { AoneAvatar } from '../../design-system';
import { useFileToken, fileSrc } from '../../utils/fileToken';
import { GROUP_TO_MODULE } from '../../config/moduleTheme';
import { ModuleThemeProvider } from '../../config/moduleTheme';
import { APP_NAME } from '../../config/brand';
import { T } from '../../design-system'; // per-module accent CSS vars

// ── Theme Context ─────────────────────────────────────────────────────────────
const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

const LIGHT = {
  bg: DS.bg,
  sidebar: '#FFFFFF',
  sidebarBorder: 'rgba(60,60,67,0.08)',
  sidebarBlur: 'none',
  // Sidebar-specific tokens (light sidebar — Apple Notes / Fitness style)
  sbText: T.text,
  sbTextSec: '#3A3A3C',
  sbMuted: '#8E8E93',
  sbHover: 'rgba(111, 44, 61,0.05)',
  sbActive: T.accentSoft,
  sbActiveText: '#591722',
  sbGroupText: '#8E8E93',
  sbBorder: 'rgba(60,60,67,0.07)',
  card: 'rgba(255,255,255,0.90)',
  cardSolid: '#FFFFFF',
  border: 'rgba(0,0,0,0.07)',
  text: '#0D0D0D',
  textSecondary: '#3A3A3A',
  textSec: '#3A3A3A',          // alias — call sites use both spellings
  textMuted: '#888888',
  // Topbar text — topbar is pearl-white in Apple theme, use dark labels
  topbarText: T.text,
  topbarTextSec: '#3A3A3C',
  topbarTextMuted: '#8E8E93',
  hover: 'rgba(111, 44, 61,0.05)',
  active: T.accentSoft,
  activeText: '#591722',
  accent: T.accent,
  accentLight: 'rgba(110, 31, 43,0.12)',
  groupText: '#8E8E93',
  topbar: 'rgba(247,249,247,0.92)',
  topbarBlur: 'blur(20px) saturate(1.6)',
  topbarShadow: '0 1px 0 rgba(0,0,0,0.05)',
  topbarBorder: 'rgba(60,60,67,0.08)',
  content: DS.bg,
  contentGradient: 'radial-gradient(ellipse 70% 50% at 15% 0%, rgba(15,124,58,0.04) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 85% 80%, rgba(95,168,106,0.02) 0%, transparent 55%)',
  // semantic pastels
  coral: '#DC5A4A',
  pink: '#D85587',
  green: T.success,
  purple: T.accent,
  dark: '#0D0D0D',
  // Semantic tokens — the component body does `const T = LIGHT`, which shadows
  // the imported design-system T. Without these, T.danger/T.info/... inside the
  // shell resolve to undefined and render as invisible text / missing badges.
  accentSoft: T.accentSoft,
  success: T.success,   successBg: T.successBg,
  warning: T.warning,   warningBg: T.warningBg,
  danger:  T.danger,    dangerBg:  T.dangerBg,
  info:    T.info,      infoBg:    T.infoBg,
};

// ── Fiori token set (opt-in via ?ui=fiori) — SAP Fiori floorplan, brand green ──
// Fiori blue is intentionally replaced by the brand green; consumed by
// FioriShellBar and FioriObjectShell. Additive only — not wired into the theme
// switcher, so it never affects the default light/dark experience.
const FIORI_THEME = {
  shell: '#2E7D5B', shellText: '#FFFFFF',
  bg: '#F5F6F7', card: '#FFFFFF',
  accent: '#2E7D5B', accentDk: '#246246', accentSoft: '#E8F5EE', accentLine: '#BFE3CD',
  purple: '#6B4FC9', purpleSoft: '#EFEAFB',
  statusCrit: '#C35500', statusNeg: T.danger, statusPos: T.success,
  line: '#E2E6EA', line2: '#EDF0F2', text: '#32363A', ink: T.text, sub: '#556070', mut: T.textMuted,
};
export { FIORI_THEME };

// ── Clean SVG icons (no emoji) ────────────────────────────────────────────────

// Icon mapping for nav items
// ── Nav group colours — Editorial Atelier, quiet per-group tint ─────────────
// Page content (headers/buttons/KPI cards) is one wine/bronze system
// everywhere (see moduleTheme.js resolveModuleAccent) — but the sidebar is
// the one place a rider keeps a small, muted per-group accent for wayfinding,
// so a fifteen-module ERP is still scannable at a glance. Reuses the same
// curated T.module palette the page-content system used to run on, just
// confined to a small icon tile + active-item ink instead of full banners.
// Unmapped groups fall back to bronze (T.module.sales), same as moduleTheme.js.
// gColors[0] = header/active ink; tile = icon.
const groupModule = (k) => DS.module[GROUP_TO_MODULE[k] || 'sales'] || DS.module.sales;
const NAV_GROUP_GRADIENTS = new Proxy({}, {
  get: (_t, k) => {
    if (typeof k !== 'string' || k === '') return null;
    const m = groupModule(k);
    return [m.accent, m.soft];
  },
});

// Rounded tile behind each item icon + active-item ink.
const NAV_GROUP_PASTEL = new Proxy({}, {
  get: (_t, k) => {
    if (typeof k !== 'string') return undefined;
    const m = groupModule(k);
    return { soft: m.soft, ink: m.accent };
  },
});


// NAV is imported from navCatalog.js — single source of truth for all nav layers.
// Layout.js (sidebar), MobileBottomNav.js (mobile grid), and command palette
// all consume the same tree. Adding a route: edit navCatalog.js only.
const NAV = NAV_CATALOG;

const ensureFonts = () => {
  if (!document.getElementById('aone-fonts')) {
    const l = document.createElement('link');
    l.id = 'aone-fonts'; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap'; // Inter = fallback only; SF Pro / system first
    document.head.appendChild(l);
  }
};

function ModuleUpgradeStrip({ upgrades, theme, onOpen }) {
  if (!Array.isArray(upgrades) || upgrades.length === 0) return null;
  const visible = upgrades.slice(0, 3);
  const toneColor = {
    success: T.accent,
    warning: '#B7791F',
    danger: T.danger,
  };
  return (
    <div style={{
      margin: '18px auto 0',
      maxWidth: 1180,
      padding: '0 24px',
      boxSizing: 'border-box',
    }}>
      <div style={{
        border: `1px solid ${theme.border}`,
        background: theme.cardSolid,
        borderRadius: 12,
        padding: 14,
        boxShadow: '0 6px 18px rgba(15,23,42,0.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ color: theme.text, fontSize: DS.type.sm, fontWeight: 800 }}>ERP controls active in this module</div>
            <div style={{ color: theme.textMuted, fontSize: DS.type.xs, marginTop: 2 }}>Contextual upgrades embedded into this existing workflow.</div>
          </div>
          <button
            type="button"
            onClick={onOpen}
            style={{
              border: `1px solid ${theme.border}`,
              background: theme.accentLight,
              color: theme.accent,
              borderRadius: 8,
              padding: '7px 10px',
              fontSize: DS.type.xs,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            View controls
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {visible.map((upgrade) => (
            <div key={upgrade.key} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              border: `1px solid ${theme.border}`,
              borderRadius: 999,
              padding: '6px 10px',
              color: theme.textSecondary,
              background: theme.content,
              fontSize: DS.type.xs,
              fontWeight: 650,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: toneColor[upgrade.tone] || theme.accent }} />
              {upgrade.label}
            </div>
          ))}
          {upgrades.length > visible.length && (
            <div style={{ color: theme.textMuted, fontSize: DS.type.xs, padding: '6px 2px' }}>+{upgrades.length - visible.length} more</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Slim connectivity bar: appears when the device drops offline, flips to a
// brief "back online" confirmation when connectivity returns. Cached data
// keeps rendering underneath — this is a status line, not a wall.
function OfflineBanner() {
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [justBack, setJustBack] = useState(false);
  useEffect(() => {
    const onDown = () => { setOffline(true); setJustBack(false); };
    const onUp = () => { setOffline(false); setJustBack(true); setTimeout(() => setJustBack(false), 3000); };
    window.addEventListener('offline', onDown);
    window.addEventListener('online', onUp);
    return () => { window.removeEventListener('offline', onDown); window.removeEventListener('online', onUp); };
  }, []);
  if (!offline && !justBack) return null;
  return (
    <div style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)',
      bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))',
      background: offline ? '#3E382F' : '#3E7A4E', color: '#fff',
      borderRadius: 999, padding: '8px 18px', fontSize: 12, fontWeight: 600,
      zIndex: 999998, boxShadow: '0 4px 18px rgba(0,0,0,0.3)', whiteSpace: 'nowrap',
    }}>
      {offline ? 'Offline — showing last synced data' : 'Back online — syncing'}
    </div>
  );
}

function ModuleUpgradeDrawer({ upgrades, theme, onClose }) {
  if (!Array.isArray(upgrades) || upgrades.length === 0) return null;
  return createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.28)', zIndex: 2500 }} />
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(520px, 100vw)',
        background: theme.cardSolid,
        zIndex: 2501,
        boxShadow: '-20px 0 50px rgba(0,0,0,0.18)',
        padding: 22,
        overflowY: 'auto',
        boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ color: theme.text, fontSize: DS.type.lg, fontWeight: 850 }}>Module Upgrade Controls</div>
            <div style={{ color: theme.textMuted, fontSize: DS.type.xs, lineHeight: 1.5, marginTop: 4 }}>These controls are mapped into the current existing module. They are not a separate module.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ border: `1px solid ${theme.border}`, background: theme.content, color: theme.textSecondary, borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}
          >
            Close
          </button>
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          {upgrades.map((upgrade) => (
            <div key={upgrade.key} style={{ border: `1px solid ${theme.border}`, borderRadius: 10, padding: 14, background: theme.content }}>
              <div style={{ color: theme.text, fontSize: DS.type.base, fontWeight: 800, marginBottom: 10 }}>{upgrade.label}</div>
              <div style={{ color: theme.textSecondary, fontSize: DS.type.xs, fontWeight: 750, marginBottom: 6 }}>Controls</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: theme.textSecondary, fontSize: DS.type.xs, lineHeight: 1.65 }}>
                {(upgrade.controls || []).map((item) => <li key={item}>{item}</li>)}
              </ul>
              <div style={{ color: theme.textSecondary, fontSize: DS.type.xs, fontWeight: 750, marginTop: 12, marginBottom: 6 }}>Next actions</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: theme.textMuted, fontSize: DS.type.xs, lineHeight: 1.65 }}>
                {(upgrade.nextActions || []).map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
}

export default function Layout({ children }) {
  const { user, logout, canAccess } = useAuth();

  // "Hand in my access": the person locks their own account. Two deliberate
  // steps — a typed LEAVE and a final confirm — because the lock is immediate
  // and only a founder can reopen it. Founders are told on Arey and by email.
  const leaveCompany = async () => {
    const typed = window.prompt(
      `This locks your ${APP_NAME} account right now: you are signed out everywhere and cannot sign in again. Only a founder can reopen it.\n\nType LEAVE to continue.`
    );
    if (typed === null) return;
    if (typed.trim().toUpperCase() !== 'LEAVE') { alert('Not locked — you did not type LEAVE.'); return; }
    const note = window.prompt('Anything you want the founders to read? (optional)', '') || '';
    if (!window.confirm('Last check: lock your account now? This cannot be undone by you.')) return;
    try {
      await api.post('/auth/leave', { confirm: 'LEAVE', note: note.trim() });
      alert('Your account is locked. The founders have been told. Thank you for your work.');
    } catch (e) {
      alert(e?.response?.data?.error || 'Could not lock the account. Ask a founder.');
      return;
    }
    logout();
  };
  const fileToken = useFileToken(); // profile photo is a raw <img> — see utils/fileToken.js
  const [fiori, setFiori] = useFioriUi();
  // Craft has no chat module: nothing unread outside the bell, no socket-driven reloads.
  const areyTotalUnread = 0;
  const reloadCountdown = null;
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // Pages that manage their own internal scrolling (a fixed conversation
  // list + message pane + composer, like Arey) need the content wrapper to
  // be a non-scrolling flex column — otherwise their own `height: 100vh`
  // inner container overflows past the top nav, and the OUTER page scrolls
  // instead of just their internal panes (the "whole page scrolls" bug).
  const isFullHeightPage = pathname === '/arey' || pathname === '/instagram-inbox' || pathname === '/aira';
  const SAP_ACCENT = `var(--aone-accent, ${DS.accent})`;
  const SAP_ACCENT_HOVER = 'var(--aone-accent-hover, #591722)';
  const SAP_ACCENT_SOFT = `var(--aone-accent-soft, ${DS.accentSoft})`;
  const SAP_ACCENT_MID = 'var(--aone-accent-mid, rgba(110, 31, 43,0.18))';
  // Active-state that understands query-string nav items (e.g. Benchmark Library
  // → /formulations?view=benchmarks). A plain item yields to a ?view= sibling
  // when the current URL carries a view param.
  const navActive = (item) => {
    const p = item.path || '';
    const qIdx = p.indexOf('?');
    const cur = new URLSearchParams(search);
    if (qIdx >= 0) {
      if (pathname !== p.slice(0, qIdx)) return false;
      const want = new URLSearchParams(p.slice(qIdx + 1));
      for (const [k, v] of want) if (cur.get(k) !== v) return false;
      return true;
    }
    const match = pathname === p || (item.altPaths && item.altPaths.includes(pathname));
    if (!match) return false;
    if (cur.get('view')) return false; // a ?view= sibling owns the active state
    return true;
  };
  const [hovered, setHovered] = useState(null);
  const [collapsed, setCollapsed] = useState(() => window.innerWidth >= 768 && window.innerWidth <= 1024);
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('aone.openGroups') || '{}'); }
    catch { return {}; }
  });
  const theme = 'light'; // app is light-only by design; no dark theme
  const [sidebarCtxMenu, setSidebarCtxMenu] = useState(null); // { x, y, path, label }

  useScreenshotPrevention();

  // ── Native shell push: register the device + wire tap deep-links ──────────
  // No-op on plain browsers; Layout only mounts authenticated, so the token
  // registers against the signed-in user.

  // ── Prefetch core page chunks on idle — eliminates lag when switching modules ──
  useEffect(() => {
    const prefetchModules = () => {
      const corePages = [
        () => import('../../pages/Dashboard'),
        () => import('../../pages/Tasks'),
        () => import('../../pages/SalesOrders'),
        () => import('../../pages/Clients'),
        () => import('../../pages/Quotations'),
      ];
      let i = 0;
      const prefetchNext = () => {
        if (i < corePages.length) {
          corePages[i]().catch((e) => { console.error('[Layout] prefetch error:', e.message); });
          i++;
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(prefetchNext, { timeout: 3000 });
          } else {
            setTimeout(prefetchNext, 500);
          }
        }
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(prefetchNext, { timeout: 5000 });
      } else {
        setTimeout(prefetchNext, 2000);
      }
    };
    // Wait 1.5s after layout mount before starting prefetch
    const timer = setTimeout(prefetchModules, 1500);
    return () => clearTimeout(timer);
  }, []);

  // ── Notifications ──────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const notifPanelRef = useRef(null);
  const prevUnreadRef = useRef(null);

  // ── Notification chime (Web Audio API — no external files) ──────────────
  const playNotifChime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [659.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.35);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.12);
        osc.stop(ctx.currentTime + i * 0.12 + 0.35);
      });
    } catch(e) { console.error('Layout error:', e.message); }
  };

  const fetchNotifications = () => {
    api.get('/notifications').then(r => {
      const newUnread = r.data?.unreadCount || 0;
      // Play chime when new notifications arrive (unread count increases)
      if (prevUnreadRef.current !== null && newUnread > prevUnreadRef.current) {
        playNotifChime();
      }
      prevUnreadRef.current = newUnread;
      setNotifications(r.data?.notifications || []);
      setUnreadCount(newUnread);
    }).catch(e => console.error('[Layout] fetchNotifications failed:', e.message));
  };

  useEffect(() => {
    // Cleanup + alert generation only once per session (not every page nav) —
    // both used to fire a server round-trip on every Layout mount.
    if (!sessionStorage.getItem('aone_alerts_generated')) {
      api.delete('/notifications/cleanup').catch(e => console.error('[Layout] cleanup notifications failed:', e.message));
      api.post('/notifications/generate-alerts').then(() => {
        sessionStorage.setItem('aone_alerts_generated', '1');
        fetchNotifications();
      }).catch(e => console.error('[Layout] generate-alerts failed:', e.message));
    }
    fetchNotifications();
    // Poll at 90s and only while the tab is visible — background tabs used to
    // hammer the API every 30s each, all day.
    const tick = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
    const interval = setInterval(tick, 90 * 1000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', tick); };
  }, []); // eslint-disable-line

  // ── Heartbeat — keeps "online" presence dot accurate in team view ──────────
  // Fires every 60s. Fire-and-forget — never blocks anything.
  useEffect(() => {
    const ping = () => api.post('/activity/heartbeat').catch(() => {});
    ping(); // immediate ping on mount
    const interval = setInterval(ping, 60_000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Close dropdown on outside click — skip clicks on any bell button or the panel itself.
  // Uses data-notif-toggle="1" attribute so all bell triggers (mobile header, bottom nav, desktop topbar) work.
  useEffect(() => {
    const handler = (e) => {
      if (e.target?.closest?.('[data-notif-toggle="1"]')) return;
      if (notifPanelRef.current && notifPanelRef.current.contains(e.target)) return;
      setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markNotifRead = async (notif) => {
    try {
      await api.put(`/notifications/${notif._id || notif.id}/read`);
      fetchNotifications();
      if (notif.link) navigate(notif.link);
      setNotifOpen(false);
    } catch(e) { console.error('Layout error:', e.message); }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      fetchNotifications();
    } catch(e) { console.error('Layout error:', e.message); }
  };

  const clearAllNotifications = async () => {
    try {
      await api.delete('/notifications/clear-all');
      setNotifications([]);
      setUnreadCount(0);
    } catch(e) { console.error('Layout error:', e.message); }
  };

  const timeAgo = (date) => {
    if (!date) return '';
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  };

  const notifIcon = (type) => {
    const c = DS.textMuted;
    if (type === 'task' || type === 'tasks') return I.tasks(c);
    if (type === 'order' || type === 'sales' || type === 'order_flow') return I.cart(c);
    if (type === 'production') return I.factory(c);
    if (type === 'alert' || type === 'warning') return I.target(c);
    if (type === 'anomaly') return I.pulse(c);
    if (type === 'gl_close') return I.banknote(c);
    if (type === 'assurance') return I.shield(c);
    if (type === 'lead') return I.trending(c);
    return I.inbox(c);
  };

  // ── Sidebar context menu: close on click outside / Escape / scroll ────
  useEffect(() => {
    if (!sidebarCtxMenu) return;
    const close = () => setSidebarCtxMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close, { once: true });
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', close, true); };
  }, [sidebarCtxMenu]);

  // ── Favorites ──────────────────────────────────────────────────────────
  const [favorites, setFavorites] = useState([]);
  const fetchFavorites = useCallback(async () => {
    try {
      const r = await api.get('/favorites');
      setFavorites(r.data || []);
    } catch(e) { console.error('[layout] favorites fetch error:', e.message); }
  }, []);
  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  const role = user?.role || 'sales';
  const isFounder = role === 'founder';
  const FINANCE_ROLES = ['founder', 'finance_manager', 'finance', 'accountant', 'hr'];
  const isFinance = FINANCE_ROLES.includes(role);
  const visibleNav = NAV.map(group => {
    if (group.founderOnly && !isFounder) return { ...group, items: [] };
    const items = group.items.filter(item => {
      if (item.founderOnly) return isFounder;
      // Hub entries (e.g. /360) — visible when the user holds ANY listed module.
      if (item.anyOf) return isFounder || item.anyOf.some(p => canAccess(p));
      if (item.financeOnly) return canAccess(item.path);
      return canAccess(item.path);
    });
    return { ...group, items };
  }).filter(g => g.items.length > 0);

  // Group containing the current route — always rendered open (accordion anchor).
  const activeGroup = (visibleNav.find(g =>
    g.items.some(navActive)
  ) || {}).group;

  // Persist which groups the user has opened across reloads.
  useEffect(() => {
    try { localStorage.setItem('aone.openGroups', JSON.stringify(openGroups)); } catch {}
  }, [openGroups]);

  // ── Command Palette (⌘K) — global search + quick nav + actions ────────────
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdQuery, setCmdQuery] = useState('');
  const [cmdResults, setCmdResults] = useState([]);
  const [cmdLoading, setCmdLoading] = useState(false);
  const [cmdSelected, setCmdSelected] = useState(0);
  const [cmdScanOpen, setCmdScanOpen] = useState(false);   // scan from inside palette
  const [headerScanOpen, setHeaderScanOpen] = useState(false); // scan from the top search bar
  const cmdInputRef = useRef(null);
  const cmdTimerRef = useRef(null);
  const [moduleUpgradeDrawerOpen, setModuleUpgradeDrawerOpen] = useState(false);

  // AI Copilot state
  const [aiMode, setAiMode] = useState(false);
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiToolsUsed, setAiToolsUsed] = useState([]);

  const AI_TRIGGERS = ['what','who','how','show','find','list','which','when','where','is ','are ','does','do ','can ','give','tell','why','count','how many','how much'];
  const isAiQuery = (q) => {
    const lower = q.toLowerCase().trim();
    return q.includes('?') || (q.split(' ').length >= 3 && AI_TRIGGERS.some(t => lower.startsWith(t)));
  };

  const askAI = async () => {
    if (!cmdQuery.trim() || aiLoading) return;
    setAiMode(true);
    setAiLoading(true);
    setAiAnswer('');
    try {
      setAiAnswer('The AI assistant is not part of Craft. Search by name or number instead.');
      setAiToolsUsed([]);
    } catch (e) {
      setAiAnswer('Sorry, I couldn\'t process that. Please try again.');
    }
    setAiLoading(false);
  };

  // Legacy global search refs (kept for topbar search bar)
  const [gsQuery, setGsQuery] = useState('');
  const [gsResults, setGsResults] = useState([]);
  const [gsLoading, setGsLoading] = useState(false);
  const [gsOpen, setGsOpen] = useState(false);
  const gsInputRef = useRef(null);
  const gsDropRef = useRef(null);
  const gsTimerRef = useRef(null);
  const [moduleUpgrades, setModuleUpgrades] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (pathname === '/arey') {
      setModuleUpgrades([]);
      return () => { cancelled = true; };
    }
    api.get('/module-upgrades', { params: { path: pathname } })
      .then((r) => {
        if (!cancelled) setModuleUpgrades(Array.isArray(r.data?.upgrades) ? r.data.upgrades : []);
      })
      .catch(() => {
        if (!cancelled) setModuleUpgrades([]);
      });
    return () => { cancelled = true; };
  }, [pathname]);

  useEffect(() => {
    setModuleUpgradeDrawerOpen(false);
  }, [pathname]);

  const doGlobalSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setGsResults([]); setGsOpen(false); return; }
    setGsLoading(true);
    try {
      const r = await api.get('/dashboard/global-search', { params: { q } });
      setGsResults(r.data?.results || []);
      setGsOpen(true);
    } catch(e) { console.error('[Layout] global search failed:', e.message); setGsResults([]); }
    setGsLoading(false);
  }, []);

  const handleGsInput = (val) => {
    setGsQuery(val);
    if (gsTimerRef.current) clearTimeout(gsTimerRef.current);
    gsTimerRef.current = setTimeout(() => doGlobalSearch(val), 300);
  };

  // Close on outside click
  useEffect(() => {
    const h = (e) => {
      if (gsDropRef.current && !gsDropRef.current.contains(e.target) &&
          gsInputRef.current && !gsInputRef.current.contains(e.target)) {
        setGsOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Quick nav items from sidebar
  const cmdNavItems = visibleNav.flatMap(g => g.items.map(i => ({
    type: 'nav', label: i.label, group: g.group || 'Core', path: i.path,
    icon: '→',
  })));

  // Quick actions
  const cmdActions = [
    { type: 'action', label: 'New Formulation', path: '/formulations', action: 'new', icon: '＋' },
    { type: 'action', label: 'New Sales Order', path: '/sales-orders', action: 'new', icon: '＋' },
    { type: 'action', label: 'New Purchase Order', path: '/purchase-orders', action: 'new', icon: '＋' },
    { type: 'action', label: 'New Sample Request', path: '/samples', action: 'new', icon: '＋' },
    { type: 'action', label: 'New Client', path: '/clients', action: 'new', icon: '＋' },
    { type: 'action', label: 'New Lead', path: '/crm/leads', action: 'new', icon: '＋' },
    { type: 'action', label: 'New Task', path: '/tasks', action: 'new', icon: '＋' },
    { type: 'action', label: 'New Vendor', path: '/vendors', action: 'new', icon: '＋' },
    { type: 'action', label: 'New RFQ', path: '/vendor-rfq', action: 'new', icon: '＋' },
    { type: 'action', label: 'Vendor Dashboard', path: '/vendor-dashboard', icon: '📊' },
    { type: 'action', label: 'Vendor Documents', path: '/vendor-doc-vault', icon: '📂' },
  ];

  // Command palette search
  const doCmdSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setCmdResults([]); return; }
    setCmdLoading(true);
    try {
      const r = await api.get('/dashboard/global-search', { params: { q } });
      setCmdResults(r.data?.results || []);
    } catch { setCmdResults([]); }
    setCmdLoading(false);
  }, []);

  const handleCmdInput = (val) => {
    setCmdQuery(val);
    setCmdSelected(0);
    setAiMode(false); setAiAnswer(''); setAiToolsUsed([]);
    if (cmdTimerRef.current) clearTimeout(cmdTimerRef.current);
    cmdTimerRef.current = setTimeout(() => doCmdSearch(val), 250);
  };

  // Favorites as palette items
  const cmdFavItems = favorites.map(f => {
    const moduleLinks = { formulation: '/formulations', sales_order: '/sales-orders', purchase_order: '/purchase-orders', sample: '/samples', client: '/clients', vendor: '/vendors', lead: '/crm/leads' };
    return { type: 'fav', label: f.label, subtitle: f.subtitle || f.module, path: moduleLinks[f.module] || '/', icon: '★' };
  });

  // Per-user usage tracking — items used more float to the top
  const cmdUsageKey = `aone_cmd_usage_${user?.id || 'anon'}`;
  const readCmdUsage = () => {
    try { return JSON.parse(localStorage.getItem(cmdUsageKey) || '{}'); } catch { return {}; }
  };
  const cmdItemKey = (item) => `${item.type}:${item.path || ''}:${item.label}`;
  const bumpCmdUsage = (item) => {
    try {
      const usage = readCmdUsage();
      const k = cmdItemKey(item);
      usage[k] = { n: (usage[k]?.n || 0) + 1, t: Date.now() };
      // cap stored entries so it never grows unbounded
      const keys = Object.keys(usage);
      if (keys.length > 200) {
        keys.sort((a, b) => (usage[a].t || 0) - (usage[b].t || 0));
        keys.slice(0, keys.length - 200).forEach(k2 => delete usage[k2]);
      }
      localStorage.setItem(cmdUsageKey, JSON.stringify(usage));
      scheduleCmdUsageSync(usage);
    } catch { /* localStorage unavailable — skip personalization */ }
  };
  // cross-device sync: pull server copy on mount (merge max-wins), push debounced on use
  const cmdUsageSyncRef = useRef(null);
  const scheduleCmdUsageSync = (usage) => {
    if (cmdUsageSyncRef.current) clearTimeout(cmdUsageSyncRef.current);
    cmdUsageSyncRef.current = setTimeout(() => {
      api.put('/dashboard/cmd-usage', { usage }).catch(() => {});
    }, 2000);
  };
  useEffect(() => {
    if (!user?.id) return;
    api.get('/dashboard/cmd-usage').then(r => {
      const remote = r.data?.usage || {};
      const local = readCmdUsage();
      const merged = { ...local };
      for (const [k, v] of Object.entries(remote)) {
        const p = merged[k];
        merged[k] = p ? { n: Math.max(p.n || 0, v.n || 0), t: Math.max(p.t || 0, v.t || 0) } : v;
      }
      try { localStorage.setItem(cmdUsageKey, JSON.stringify(merged)); } catch {}
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  // score = count with a half-life decay (~30 days), so stale habits fade
  const cmdUsageScore = (usage, item) => {
    const u = usage[cmdItemKey(item)];
    if (!u) return 0;
    const ageDays = (Date.now() - (u.t || 0)) / 86400000;
    return u.n * Math.pow(0.5, ageDays / 30);
  };
  const sortByUsage = (usage, items) => items
    .map((it, idx) => ({ it, idx, s: cmdUsageScore(usage, it) }))
    .sort((a, b) => b.s - a.s || a.idx - b.idx)
    .map(x => x.it);

  // Build filtered list for palette
  const cmdFiltered = (() => {
    const usage = readCmdUsage();
    const q = cmdQuery.toLowerCase().trim();
    if (!q) {
      // no query — show most-used items first, then the usual defaults
      return sortByUsage(usage, [...cmdFavItems.slice(0, 5), ...cmdActions, ...cmdNavItems]).slice(0, 18);
    }
    const navMatch = sortByUsage(usage, cmdNavItems.filter(i => (i.label || '').toLowerCase().includes(q) || (i.group || '').toLowerCase().includes(q)));
    const actMatch = sortByUsage(usage, cmdActions.filter(i => (i.label || '').toLowerCase().includes(q)));
    const favMatch = sortByUsage(usage, cmdFavItems.filter(i => (i.label || '').toLowerCase().includes(q)));
    // API results converted to items
    const apiItems = cmdResults.flatMap(g => g.items.map(i => ({
      type: 'result', label: i.title, subtitle: i.subtitle, path: i.link, icon: g.icon || '◉', group: g.category,
    })));
    return sortByUsage(usage, [...favMatch, ...actMatch, ...navMatch.slice(0, 8)]).concat(sortByUsage(usage, apiItems));
  })();

  const executeCmdItem = (item) => {
    bumpCmdUsage(item);
    setCmdOpen(false); setCmdQuery(''); setCmdResults([]);
    if (item.path) navigate(item.path);
  };

  // Cmd+K / Ctrl+K shortcut → open command palette
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(prev => !prev);
        setCmdQuery(''); setCmdResults([]); setCmdSelected(0);
        setAiMode(false); setAiAnswer('');
      }
      if (e.key === 'Escape') { setCmdOpen(false); setGsOpen(false); }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  // Keyboard nav inside palette
  useEffect(() => {
    if (!cmdOpen) return;
    const h = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCmdSelected(p => Math.min(p + 1, cmdFiltered.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCmdSelected(p => Math.max(p - 1, 0)); }
      if (e.key === 'Enter' && cmdFiltered[cmdSelected]) { e.preventDefault(); executeCmdItem(cmdFiltered[cmdSelected]); }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [cmdOpen, cmdSelected, cmdFiltered]);

  // Focus input when palette opens
  useEffect(() => {
    if (cmdOpen) setTimeout(() => cmdInputRef.current?.focus(), 50);
  }, [cmdOpen]);

  const T = LIGHT;

  useEffect(() => { ensureFonts(); }, []);

  useEffect(() => {
    NAV.forEach(g => {
      if (g.items.some(i => i.path === pathname)) {
        setOpenGroups(prev => ({ ...prev, [g.group]: true }));
      }
    });
  }, [pathname]);

  const toggleGroup = (group) => {
    if (!group) return;
    // Accordion: opening a group collapses the others; the active group stays
    // open implicitly (handled in render), so it's never trapped closed.
    setOpenGroups(prev => (prev[group] ? {} : { [group]: true }));
  };

  // Profile photo state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null); // current user's photo
  const [profileUploading, setProfileUploading] = useState(false);
  const profileFileRef = useRef(null);

  // Load user photo on mount
  useEffect(() => {
    api.get('/auth/me').then(r => {
      if (r.data?.photo) setProfilePhoto(r.data.photo);
    }).catch(e => console.error('[Layout] load user photo failed:', e.message));
  }, []); // eslint-disable-line

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Photo must be under 2MB'); e.target.value = ''; return; }
    if (!file.type.startsWith('image/')) { alert('Please select an image file'); e.target.value = ''; return; }
    setProfileUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const photo = ev.target.result;
        await api.put('/auth/update-profile', { photo });
        setProfilePhoto(photo);
      } catch (err) { alert(err.response?.data?.error || 'Failed to upload photo'); }
      setProfileUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const removeProfilePhoto = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    try {
      await api.put('/auth/update-profile', { photo: '' });
      setProfilePhoto(null);
    } catch (err) { alert('Failed to remove photo'); }
  };

  // Responsive detection comes from the shared hook, not a private copy.
  //
  // The shell used to hold its own window.innerWidth listener — a fourth
  // implementation of one decision, alongside useResponsive, AoneDataTable and
  // the page shells. It is why the preview gallery drew the DESKTOP sidebar
  // inside a 390px phone frame: the frame told the page it was on a phone and
  // the shell around it never heard. One source, so a subtree told it is 390px
  // wide is 390px wide all the way down.
  //
  // The tablet ceiling moves 1180 → 1199 with this. iPad Pro 11" landscape is
  // 1194 and was the reason for the old number; it stays a tablet either way.
  const { isMobile, isTablet } = useResponsive();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Bumped to open the mobile App Finder sheet (module menu) from the header's
  // waffle icon or an edge swipe — a counter, not a boolean, so repeat taps
  // always re-open it even if MobileBottomNav already closed its own sheet.
  const [menuTrigger, setMenuTrigger] = useState(0);
  // Detect native Capacitor app for iOS-specific enhancements
  const isCapacitor = !!(window.Capacitor?.isNativePlatform?.());
  const isIOS = isCapacitor && window.Capacitor?.getPlatform?.() === 'ios';
  useEffect(() => { if (!isMobile) setMobileMenuOpen(false); }, [isMobile]);

  // Auto-collapse sidebar when entering tablet range
  useEffect(() => { if (isTablet) setCollapsed(true); }, [isTablet]);

  const sideW = collapsed ? 72 : 280;

  // Floating assistants (Jarvis/Arey) anchor bottom-LEFT of the content area so
  // they never cover right-aligned row actions or sticky action-bar buttons.
  useEffect(() => {
    document.documentElement.style.setProperty('--aone-sidebar-w', isMobile ? '0px' : `${sideW}px`);
  }, [sideW, isMobile]);

  // ── PULL-TO-REFRESH (tablet + mobile) ────────────────────────────────
  const contentRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullStartY = useRef(0);
  const isPulling = useRef(false);
  const PULL_THRESHOLD = 80;

  useEffect(() => {
    const el = contentRef.current;
    if (!el || (!isMobile && !isTablet)) return;

    const onTouchStart = (e) => {
      if (el.scrollTop <= 0 && !isRefreshing) {
        pullStartY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    };

    const onTouchMove = (e) => {
      if (!isPulling.current || isRefreshing) return;
      const dy = e.touches[0].clientY - pullStartY.current;
      if (dy > 0 && el.scrollTop <= 0) {
        // Dampen the pull (rubber band effect)
        setPullDistance(Math.min(dy * 0.4, 120));
      } else {
        isPulling.current = false;
        setPullDistance(0);
      }
    };

    const onTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      if (pullDistance >= PULL_THRESHOLD) {
        setIsRefreshing(true);
        setPullDistance(PULL_THRESHOLD * 0.5);
        // Reload page data by dispatching a custom event pages can listen to
        window.dispatchEvent(new CustomEvent('aone-pull-refresh'));
        // Also reload the current route
        setTimeout(() => {
          window.location.reload();
        }, 600);
      } else {
        setPullDistance(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, isTablet, isRefreshing, pullDistance]);

  // ── SWIPE GESTURES (tablet + mobile) ─────────────────────────────────
  useEffect(() => {
    if (!isMobile && !isTablet) return;
    let startX = 0, startY = 0, tracking = false;
    const SWIPE_THRESHOLD = 60;       // min px to trigger
    const SWIPE_MAX_Y = 80;           // max vertical drift
    const EDGE_ZONE = 40;             // px from left edge to start swipe-open

    const onTouchStart = (e) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      // Only track if starting from left edge (open) or anywhere (close)
      tracking = isMobile
        ? (startX < EDGE_ZONE || mobileMenuOpen)
        : (startX < EDGE_ZONE + sideW || !collapsed);
    };

    const onTouchEnd = (e) => {
      if (!tracking) return;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);
      if (dy > SWIPE_MAX_Y) return; // too vertical — likely scrolling

      if (dx > SWIPE_THRESHOLD) {
        // Swipe RIGHT → open the App Finder sheet (mobile has no sidebar)
        if (isMobile) { setMobileMenuOpen(true); setMenuTrigger(t => t + 1); }
        else if (isTablet) setCollapsed(false);
      } else if (dx < -SWIPE_THRESHOLD) {
        // Swipe LEFT → close sidebar
        if (isMobile) setMobileMenuOpen(false);
        else if (isTablet) setCollapsed(true);
      }
      tracking = false;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, isTablet, mobileMenuOpen, collapsed, sideW]);
  const PAGE_TITLES = {
    '/': 'Dashboard', '/skus': 'SKU Master', '/brands': 'Brands Master', '/trademarks': 'Trademarks', '/creative/library': 'Asset Library', '/creative/approval-inbox': 'Approval Inbox', '/creative/brand-assets': 'Brand Assets', '/creative/color-library': 'Color Library', '/creative/photography': 'Photography', '/creative/collections': 'Collections', '/creative/calendar': 'Content Calendar', '/creative/influencers': 'Influencers', '/creative/templates': 'Templates', '/crm/leads': 'Leads (CRM)',
    '/rawmaterials': 'Ingredients / Raw Materials', '/ai-formulator': 'AI Formulator',
    '/qms/incoming': 'QMS Incoming', '/qms/inprocess': 'QMS In-Process', '/qms/release': 'QMS Release',
    '/finished-goods': 'Finished Goods', '/sales-orders': 'Sales Orders', '/purchase-orders': 'Purchase Orders',
    '/rm-inwards': 'RM Inwards', '/pkg-inwards': 'Packaging Inwards', '/label-inwards': 'Label Inwards', '/ready-goods-inwards': 'Ready Goods Inwards', '/master-data-hygiene': 'Master Data Hygiene', '/client360': 'Client 360', '/sku360': 'SKU 360', '/vendor360': 'Vendor 360', '/supplier-audits': 'Supplier Audits',
    '/vendor-dashboard': 'Vendor Command Center', '/vendor-doc-vault': 'Vendor Document Vault', '/vendor-rfq': 'RFQ Management',
    '/users': 'Access Control', '/module-access': 'Module Access', '/hr': 'HR & People', '/quotations': 'Quotations',
    '/service-tickets': 'Service Tickets',
    '/customer-care': 'Customer Care',
    '/customer-360': 'Customer 360',
    '/360': '360 Directory', '/360/client': 'Client 360', '/360/customer': 'Customer 360', '/360/sku': 'SKU 360', '/360/vendor': 'Vendor 360',
    '/lovesquad-hub': 'Creators', '/lovesquad-hub/squad': 'Squad Hub', '/lovesquad-hub/ambassadors': 'Ambassadors', '/lovesquad-hub/payouts': 'Payouts', '/lovesquad-hub/rewards': 'Hearts & Rewards', '/lovesquad-hub/settings': 'Program Settings',
    '/returns': 'Returns',
    '/knowledge-base': 'Knowledge Base',
    '/lookbook': 'LookBook',
    '/shopify-stores': 'Shopify Stores',
    '/shopify-dashboard': 'Shopify Orders Dashboard',
    '/contracts': 'Contracts',
    '/territory': 'Territory',
    '/forecast': 'Sales Forecast',
    '/cpq-rules': 'CPQ Rules',
    '/linkedin': 'LinkedIn',
    '/salesforce': 'Salesforce',
    '/salesforce/follow-ups': 'Follow-ups Tracker',
    '/salesforce/pipeline': 'Pipeline Health',
    '/salesforce/sequences': 'Sequences',
    '/partner-admin': 'Exports',
  };
  // Object routes (/samples/:id, /deals/:id …) end in an opaque id. Title-casing
  // it produced headers like "Samples / Da315e30 11cf 4f49 Ad0d" — the record's
  // own page already names it properly, so title from the parent instead.
  const titleFrom = (p) => p.replace(/^\//, '').replace(/-/g, ' ').replace(/\//g, ' / ').replace(/\b\w/g, l => l.toUpperCase());
  const parentPath = pathname.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
  const pageTitle = PAGE_TITLES[pathname] || PAGE_TITLES[parentPath] || titleFrom(parentPath);

  const getIcon = (path, color) => {
    const key = ICON_MAP[path] || 'file';
    const fn = I[key];
    return fn ? fn(color) : null;
  };

  return (
    <ThemeContext.Provider value={{ theme, T }}>
    <ModuleThemeProvider>
    {/* SAP Fiori — Tablet + Mobile responsive styles */}
    <style>{`
      /* ── Global design tokens (SAP Fiori + brand) ── */
      :root {
        --fiori-shell:        ${DS.accent};
        --fiori-shell-text:   ${DS.textInv};
        --fiori-bg:           ${DS.bg};
        --fiori-surface:      ${DS.surface};
        --fiori-border:       ${DS.border};
        --fiori-border-strong:${DS.borderStrong};
        --fiori-text:         ${DS.text};
        --fiori-text-sec:     ${DS.textSec};
        --fiori-text-muted:   ${DS.textMuted};
        --fiori-accent:       ${DS.accent};
        --fiori-accent-soft:  ${DS.accentSoft};
        --fiori-accent-hover: ${DS.accentHover};
        --fiori-radius-sm:    ${DS.r.sm}px;
        --fiori-radius:       ${DS.r.md}px;
        --fiori-radius-lg:    ${DS.r.lg}px;
        --fiori-shadow:       ${DS.shadow.md};
        --fiori-shadow-md:    ${DS.shadow.lg};
        --fiori-surface-alt:  ${DS.surfaceAlt};
        --fiori-divider:      ${DS.divider};
      }

      /* ── SAP Fiori module header ── */
      .aone-module-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 24px;
        flex-wrap: wrap;
      }
      .aone-module-title {
        font-size: ${DS.type.xxl}px;
        font-weight: 700;
        color: var(--fiori-text);
        letter-spacing: -0.01em;
        line-height: 1.3;
        font-family: ${DS.font};
      }
      .aone-module-subtitle {
        font-size: ${DS.type.base}px;
        color: var(--fiori-text-muted);
        font-weight: 400;
        margin-top: 3px;
      }

      /* ── Page content wrapper ── */
      .aone-page-content {
        padding: 24px 32px 80px;
        background: var(--fiori-bg);
        min-height: 100%;
        font-family: ${DS.font};
      }

      /* ── SAP Fiori Card — clean white with border ── */
      .aone-card {
        background: var(--fiori-surface);
        border-radius: var(--fiori-radius-lg);
        border: 1px solid var(--fiori-border);
        box-shadow: var(--fiori-shadow);
        padding: 20px 24px;
      }

      /* ── Section labels ── */
      .aone-section-label {
        font-size: ${DS.type.xs}px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--fiori-text-muted);
        margin-bottom: 8px;
        font-family: ${DS.font};
      }

      /* ── Buttons — SAP Fiori Emphasized (primary) ── */
      .aone-btn-primary {
        background: var(--fiori-accent);
        color: #fff;
        border: 1px solid var(--fiori-accent);
        border-radius: var(--fiori-radius);
        padding: 0 16px;
        height: 36px;
        font-size: ${DS.type.md}px;
        font-weight: 600;
        cursor: pointer;
        font-family: ${DS.font};
        transition: background 0.15s ease, border-color 0.15s ease;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
      }
      .aone-btn-primary:hover { background: var(--fiori-accent-hover); border-color: var(--fiori-accent-hover); }
      .aone-btn-primary:active { opacity: 0.88; }

      /* ── Buttons — SAP Fiori Standard (secondary) ── */
      .aone-btn-secondary {
        background: var(--fiori-surface);
        color: var(--fiori-text);
        border: 1px solid var(--fiori-border-strong);
        border-radius: var(--fiori-radius);
        padding: 0 16px;
        height: 36px;
        font-size: ${DS.type.md}px;
        font-weight: 500;
        cursor: pointer;
        font-family: ${DS.font};
        transition: background 0.15s ease, border-color 0.15s ease;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
      }
      .aone-btn-secondary:hover { background: var(--fiori-bg); border-color: var(--fiori-accent); }
      .aone-btn-secondary:active { opacity: 0.88; }

      /* ── SAP Fiori Compact Table ── */
      .aone-table-wrap th {
        font-size: ${DS.type.sm}px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--fiori-text-sec);
        padding: 10px 16px;
        background: var(--fiori-surface-alt);
        border-bottom: 1px solid var(--fiori-border-strong);
        border-top: none;
        white-space: nowrap;
        text-align: left;
        position: sticky;
        top: 0;
        z-index: 1;
      }
      .aone-table-wrap td {
        font-size: ${DS.type.base}px;
        color: var(--fiori-text);
        padding: 10px 16px;
        border-bottom: 1px solid var(--fiori-border);
        border-top: none;
        font-family: ${DS.font};
        line-height: 1.4;
      }
      .aone-table-wrap tr { transition: background 0.1s; }
      .aone-table-wrap tbody tr:hover { background: var(--fiori-accent-soft); }
      .aone-table-wrap tr:last-child td { border-bottom: none; }

      /* ── Status chips ── */
      .aone-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border-radius: ${DS.r.pill}px;
        font-size: ${DS.type.xs}px;
        font-weight: 700;
        font-family: ${DS.font};
        letter-spacing: 0.01em;
      }

      /* ── Input focus ring — Fiori standard ── */
      input:focus, select:focus, textarea:focus {
        border-color: var(--fiori-accent) !important;
        box-shadow: 0 0 0 2px ${DS.accentMid} !important;
        outline: none !important;
      }

      /* ── KPI / stat cards — Fiori Information Tile style ── */
      .aone-kpi-card {
        background: var(--fiori-surface);
        border-radius: var(--fiori-radius-lg);
        border: 1px solid var(--fiori-border);
        box-shadow: var(--fiori-shadow);
        padding: 16px 18px;
      }
      .aone-kpi-card .kpi-value {
        font-size: ${DS.type.huge}px;
        font-weight: 700;
        color: var(--fiori-text);
        line-height: 1.1;
        font-family: ${DS.font};
        letter-spacing: -0.02em;
      }
      .aone-kpi-card .kpi-label {
        font-size: ${DS.type.xs}px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--fiori-text-muted);
        margin-top: 4px;
      }

      /* ── Pull-to-refresh indicator ── */
      .aone-pull-indicator {
        background: var(--fiori-accent-soft);
        border: 1px solid rgba(110, 31, 43,0.20);
        color: var(--fiori-accent);
      }

      /* ── Mobile page transition — native-style slide-up + fade on navigation ── */
      /* Opacity only — deliberately no transform.
         A transform on this wrapper makes it the containing block for every
         position:fixed descendant, so each modal, sheet and dropdown inside a
         mobile page anchors to the page instead of the viewport. Measured on
         production, the New SKU modal opened at top 335254px: it was never
         broken, it was just far below the fold, which is indistinguishable
         from a dead button. Opacity creates a stacking context but NOT a
         containing block, so the fade survives and fixed positioning works.
         Do not reintroduce translate here. */
      @keyframes aoneMPageIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      /* will-change must NOT live here permanently. It creates a containing
         block for position:fixed descendants for the element's whole lifetime,
         not just while the 0.34s animation runs — so every modal, sheet and
         floating bar inside a mobile page anchors to the page instead of the
         viewport and renders far below the fold. That is what made overlays
         across the app look like they simply did not open.
         The animation still composites fine without the hint: an animation on
         transform/opacity is GPU-promoted while it runs. */
      .aone-mpage { animation: aoneMPageIn 0.34s cubic-bezier(0.32, 0.72, 0, 1); }
      @media (prefers-reduced-motion: reduce) {
        .aone-mpage { animation: none !important; }
      }

      /* ── TABLET LANDSCAPE / SMALL DESKTOP (1025px – 1199px) ──── */
      @media (min-width: 1025px) and (max-width: 1199px) {
        .aone-main-grid { grid-template-columns:1fr 1fr!important; }
        .aone-kpi-grid { grid-template-columns:repeat(4,1fr)!important; }
        .aone-ecosystem-grid { grid-template-columns:repeat(3,1fr)!important; }
        .aone-dash-padding { padding:20px 28px!important; }
        .aone-split-master { width:45%!important; flex-shrink:0!important; }
        .aone-split-detail { width:55%!important; flex-shrink:0!important; }
        .aone-split-container { display:flex!important; gap:16px!important; align-items:flex-start!important; }
      }

      /* ── TABLET (768px – 1024px) — SAP Fiori tablet layout ──── */
      @media (min-width: 768px) and (max-width: 1024px) {
        .aone-main-grid { grid-template-columns:1fr 1fr!important; }
        .aone-analytics-grid { grid-template-columns:1fr!important; }
        .aone-kpi-grid { grid-template-columns:repeat(3,1fr)!important; gap:12px!important; }
        .aone-form-grid { grid-template-columns:1fr 1fr!important; }
        .aone-import-grid { grid-template-columns:1fr 1fr!important; }
        .aone-ecosystem-grid { grid-template-columns:repeat(3,1fr)!important; }

        /* Fiori 44px touch targets */
        .aone-main-grid button, .aone-kpi-grid > div { min-height:44px!important; }

        /* Tablet padding */
        .aone-dash-padding { padding:20px 24px!important; }
        .aone-page-content { padding:20px 24px 64px!important; }
        .aone-form-container { padding:20px!important; }

        /* Tablet module title */
        .aone-module-title { font-size:${DS.type.xl}px!important; }

        /* Fiori modal sizing for tablet */
        .aone-modal-content { width:calc(100vw - 64px)!important; max-width:640px!important; max-height:88vh!important; overflow-y:auto!important; border-radius:${DS.r.lg}px!important; }

        /* Table: horizontal scroll on tablet */
        .aone-table-wrap { overflow-x:auto!important; -webkit-overflow-scrolling:touch; }
        .aone-table-wrap th { font-size:${DS.type.xs}px!important; padding:9px 14px!important; }
        .aone-table-wrap td { font-size:${DS.type.base}px!important; padding:9px 14px!important; }

        /* Tablet actions wrap */
        .aone-module-actions { flex-wrap:wrap!important; gap:8px!important; }

        /* Greeting */
        .aone-greeting-title { font-size:${DS.type.huge}px!important; }

        /* Quick nav */
        .aone-quick-nav { overflow-x:auto!important; -webkit-overflow-scrolling:touch; }
        .aone-quick-nav::-webkit-scrollbar { display:none; }

        /* Nav links: Fiori touch target */
        .aone-nav-link { min-height:44px!important; padding:10px 14px!important; }

        /* Search */
        .aone-search-input { height:40px!important; font-size:${DS.type.md}px!important; }

        /* Attendance */
        .aone-attendance-row button { min-height:44px!important; }

        /* Split panel: slide-in from right */
        .aone-split-container { display:flex!important; gap:0!important; }
        .aone-split-master { width:100%!important; transition:width 0.25s ease!important; }
        .aone-split-detail {
          position:fixed!important; right:0!important; top:0!important; bottom:0!important;
          width:400px!important; z-index:900!important;
          box-shadow:-2px 0 16px rgba(0,0,0,0.12)!important;
          overflow-y:auto!important; -webkit-overflow-scrolling:touch;
          border-left:1px solid var(--fiori-border)!important;
        }
        .aone-split-master.has-detail { width:calc(100% - 400px)!important; }
      }

      /* ── TABLET LANDSCAPE (768-1024, landscape) ── */
      @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
        .aone-main-grid { grid-template-columns:1fr 1fr!important; }
        .aone-kpi-grid { grid-template-columns:repeat(4,1fr)!important; }
        .aone-split-detail { width:50%!important; position:relative!important; box-shadow:none!important; border-left:1px solid var(--fiori-border)!important; }
        .aone-split-master.has-detail { width:50%!important; }
      }

      /* ── MOBILE (<768px) — SAP Fiori mobile layout ─────────────── */
      @media (max-width: 767px) {
        .aone-sidebar-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:998; }
        .aone-sidebar-mobile { position:fixed!important; left:0; top:0; bottom:0; width:280px!important; z-index:999; box-shadow:4px 0 20px rgba(0,0,0,0.22); }
        .aone-main-content { padding:12px!important; }
        .aone-topbar { padding:0 12px!important; }
        .aone-kpi-grid { grid-template-columns:repeat(2,1fr)!important; gap:10px!important; }
        .aone-main-grid { grid-template-columns:1fr!important; }
        .aone-analytics-grid { grid-template-columns:1fr!important; }
        .aone-hide-mobile { display:none!important; }
        .aone-notif-dropdown { width:calc(100vw - 16px)!important; right:8px!important; left:8px!important; max-width:none!important; }
        .aone-module-header { flex-direction:column!important; align-items:stretch!important; gap:10px!important; }
        .aone-module-title { font-size:${DS.type.lg}px!important; font-weight:700!important; }
        .aone-module-actions { justify-content:stretch!important; flex-wrap:wrap!important; gap:8px!important; }
        .aone-module-actions button { flex:1!important; justify-content:center!important; min-height:44px!important; }
        .aone-form-grid { grid-template-columns:1fr!important; }
        .aone-form-grid-3 { grid-template-columns:1fr!important; }
        .aone-form-container { padding:16px!important; }
        .aone-page-content { padding:12px 12px 80px!important; }
        .aone-import-grid { grid-template-columns:1fr!important; }
        .aone-table-wrap { margin:0 -12px; border-radius:0!important; border-left:none!important; border-right:none!important; overflow-x:auto!important; -webkit-overflow-scrolling:touch; }
        .aone-table-wrap table { min-width:520px; }
        .aone-table-wrap th { font-size:${DS.type.xxs}px!important; padding:8px 12px!important; }
        .aone-table-wrap td { font-size:${DS.type.sm}px!important; padding:8px 12px!important; }
        .aone-search-input { width:100%!important; min-height:44px!important; }
        .aone-modal-content { width:calc(100vw - 16px)!important; max-height:94vh!important; overflow-y:auto!important; padding:16px!important; border-radius:${DS.r.xl}px ${DS.r.xl}px 0 0!important; }

        /* Typography */
        .aone-greeting-title { font-size:${DS.type.xxl}px!important; }
        .aone-dash-header { flex-direction:column!important; gap:10px!important; }
        .aone-dash-padding { padding:12px!important; }

        /* Touch targets — Fiori 44px minimum */
        button, [role="button"], select { min-height:44px!important; }
        input[type="text"], input[type="search"], input[type="email"], input[type="number"],
        input:not([type]), textarea, select { font-size:16px!important; }

        /* Filter chips: horizontal scroll */
        .aone-filter-row {
          display:flex!important; overflow-x:auto!important; flex-wrap:nowrap!important;
          -webkit-overflow-scrolling:touch; gap:8px!important; padding-bottom:4px;
        }
        .aone-filter-row::-webkit-scrollbar { display:none; }
        .aone-filter-row > * { flex-shrink:0!important; }

        /* Tabs: horizontal scroll */
        .aone-tabs { overflow-x:auto!important; flex-wrap:nowrap!important; -webkit-overflow-scrolling:touch; }
        .aone-tabs::-webkit-scrollbar { display:none; }
        .aone-tabs button, .aone-tabs [role="tab"] { flex-shrink:0!important; white-space:nowrap!important; }

        /* Cards: full width */
        .aone-card { min-width:0!important; width:100%!important; border-radius:${DS.r.lg}px!important; padding:14px!important; }

        /* KPI cards */
        .aone-kpi-card { padding:12px 14px!important; border-radius:${DS.r.md}px!important; }
        .aone-kpi-card .kpi-value { font-size:${DS.type.xxl}px!important; }
        .aone-kpi-card .kpi-label { font-size:${DS.type.xxs}px!important; }

        /* Buttons: full-width on mobile */
        .aone-btn-primary, .aone-btn-secondary { min-height:44px!important; font-size:${DS.type.md}px!important; width:100%!important; justify-content:center!important; border-radius:${DS.r.md}px!important; }

        /* Section label */
        .aone-section-label { font-size:${DS.type.xxs}px!important; }

        /* Right panels: full-screen */
        .aone-right-panel, .aone-detail-panel {
          position:fixed!important; inset:0!important; width:100%!important;
          border-radius:0!important; z-index:950!important;
        }

        /* Split: stack vertically */
        .aone-split-container { flex-direction:column!important; }
        .aone-split-master, .aone-split-detail { width:100%!important; position:relative!important; box-shadow:none!important; }

        /* Drawer */
        .aone-drawer { width:100vw!important; }

        /* Badge / chip */
        .aone-chip, .aone-badge { white-space:normal!important; }

        /* Stat rows */
        .aone-stat-row { flex-direction:column!important; gap:8px!important; }

        /* Ecosystem: compact horizontal scroll */
        .aone-ecosystem-wrap { padding:0 12px 12px!important; }
        .aone-ecosystem-wrap .dce { padding:14px!important; }
        .aone-ecosystem-header { margin-bottom:12px!important; }
        .aone-ecosystem-grid {
          display:flex!important; overflow-x:auto!important; -webkit-overflow-scrolling:touch;
          gap:10px!important; padding-bottom:6px!important; scroll-snap-type:x mandatory;
        }
        .aone-ecosystem-grid::-webkit-scrollbar { display:none; }
        .aone-ecosystem-grid > div { flex:0 0 120px!important; scroll-snap-align:start; }
        .aone-ecosystem-grid > div > div:first-child { height:36px!important; }
        .aone-ecosystem-grid > div > div:last-child { padding:8px 10px!important; }
        .aone-ecosystem-grid > div > div:last-child > div:first-child { font-size:${DS.type.xs}px!important; }
        .aone-ecosystem-grid > div > div:last-child > div:nth-child(2) { display:none!important; }
        .aone-ecosystem-grid > div > div:last-child > div:last-child > span { font-size:${DS.type.xxs}px!important; }
        .aone-quick-nav { overflow-x:auto!important; flex-wrap:nowrap!important; -webkit-overflow-scrolling:touch; padding-bottom:4px; }
        .aone-quick-nav::-webkit-scrollbar { display:none; }
        .aone-quick-nav button { white-space:nowrap!important; flex-shrink:0!important; }
        .aone-attendance-row { flex-direction:column!important; gap:10px!important; align-items:stretch!important; padding:14px 16px!important; }
        .aone-attendance-row button { width:100%!important; text-align:center!important; justify-content:center!important; }
        .aone-team-section { max-height:none!important; }

        /* Invoice / document */
        .aone-invoice-wrap { transform-origin:top left; overflow-x:auto!important; }
        .aone-print-preview { padding:12px!important; }

        /* Dialogs */
        [role="dialog"] { max-width:calc(100vw - 16px)!important; margin:8px!important; }
      }

      @media (max-width: 480px) {
        .aone-kpi-grid { grid-template-columns:1fr!important; }
        .aone-ecosystem-grid > div { flex:0 0 100px!important; }
        .aone-ecosystem-grid > div > div:first-child { height:30px!important; }
        .aone-ecosystem-grid > div > div:first-child span { font-size:16px!important; }
        .aone-page-content { padding:8px 8px 80px!important; }
        .aone-dash-padding { padding:8px!important; }
        .aone-form-container { padding:12px!important; }
        .aone-module-actions button { font-size:${DS.type.base}px!important; padding:0 12px!important; }
      }

      /* ── Dynamic Viewport Height — iOS URL bar fix ── */
      .aone-root-wrap {
        height: 100vh;
        height: 100dvh;
      }
      .aone-root-wrap,
      .aone-root-wrap > div,
      #aone-content {
        min-height: 0;
      }
      #aone-content {
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
      }
    `}</style>
    <div className={`aone-root-wrap${isCapacitor ? ' aone-native-app' : ''}${isIOS ? ' aone-native-ios' : ''}${isTablet ? ' aone-tablet-shell' : ''}`} style={{ display: 'flex', background: T.bg, fontFamily: DS.font, overflow: 'hidden', minHeight: 0 }}>

      {/* ── Sidebar (desktop only) ────────────────────────────────────────── */}
      {!isMobile && !fiori && (
      <div style={{
        width: sideW, flexShrink: 0,
        background: `linear-gradient(180deg, ${DS.surface} 0%, ${DS.bg} 100%)`,
        backdropFilter: T.sidebarBlur, WebkitBackdropFilter: T.sidebarBlur,
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.25s cubic-bezier(.4,0,.2,1)',
        overflow: 'hidden',
        borderRight: `1px solid ${DS.divider}`,
      }}>

        {/* Logo + Collapse Toggle */}
        <div style={{
          height: 52, display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '0' : '0 12px 0 22px',
          borderBottom: `1px solid ${T.sbBorder}`,
          flexShrink: 0,
        }}>
          {collapsed
            ? <img src="/logo.png" alt={APP_NAME} title="Expand sidebar" onClick={() => setCollapsed(false)}
                style={{ width: 30, height: 30, cursor: 'pointer', display: 'block' }} />
            : <>
                <div
                  onClick={() => navigate('/')}
                  title="Go to Home"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                >
                  <img src="/logo.png" alt="" aria-hidden="true" style={{ width: 28, height: 28, display: 'block' }} />
                  <span style={{
                    fontFamily: DS.fontDisplay,
                    fontStyle: 'normal',
                    fontSize: DS.type.xl, fontWeight: 600, color: T.sbText, letterSpacing: '-0.025em',
                  }}>{APP_NAME}</span>
                </div>
                <button onClick={() => setCollapsed(true)} title="Collapse sidebar" style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px',
                  borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: T.sbMuted, transition: 'all 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.sbHover; e.currentTarget.style.color = T.sbText; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.sbMuted; }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
                </button>
              </>
          }
        </div>

        {/* Navigation */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0', overflowX: 'hidden' }}>

          {/* Favorites section */}
          {!collapsed && favorites.length > 0 && (
            <div style={{ marginBottom: 12, padding: '0 12px' }}>
              <div style={{ fontSize: DS.type.xxs, fontWeight: 700, color: T.sbMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, padding: '0 4px' }}>Favorites</div>
              {favorites.slice(0, 8).map(f => {
                const moduleLinks = { formulation: '/formulations', sales_order: '/sales-orders', purchase_order: '/purchase-orders', sample: '/samples', client: '/clients', vendor: '/vendors', lead: '/crm/leads' };
                return (
                  <div key={f.id} onClick={() => navigate(moduleLinks[f.module] || '/')}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 8, cursor: 'pointer', transition: 'background 0.1s', fontSize: DS.type.xs, color: T.sbTextSec }}
                    onMouseEnter={e => e.currentTarget.style.background = T.sbHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <span style={{ color: T.warning, fontSize: DS.type.xs }}>★</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{f.label}</span>
                  </div>
                );
              })}
              <div style={{ height: 1, background: T.sbBorder, margin: '8px 0 4px' }} />
            </div>
          )}

          {visibleNav.map(g => {
            const isGroupOpen = !g.group || g.group === activeGroup || openGroups[g.group] === true;
            const hasActiveItem = g.items.some(navActive);
            return (
              <div key={g.group} style={{ marginBottom: 4 }}>
                {!collapsed && g.group && (() => {
                  const gColors = NAV_GROUP_GRADIENTS[g.group];
                  return (
                  <div onClick={() => toggleGroup(g.group)}
                    style={{
                      padding: '20px 22px 6px',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      userSelect: 'none',
                    }}>
                    <span style={{
                      fontFamily: "'Playfair Display', serif",
                      fontStyle: 'normal',
                      fontSize: DS.type.md, fontWeight: 600,
                      color: hasActiveItem && gColors ? gColors[0] : T.sbText,
                      letterSpacing: '-0.01em',
                    }}>
                      {g.group.toLowerCase().replace(/^(.)|\s(.)/g, m => m.toUpperCase())}.
                    </span>
                    <span style={{
                      width: 22, height: 22, borderRadius: 7,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: T.sbMuted,
                    }}>
                      <svg width="10" height="10" viewBox="0 0 10 10" style={{ transition: 'transform 0.2s', transform: isGroupOpen ? 'rotate(0)' : 'rotate(-90deg)' }}>
                        <path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  </div>
                  );
                })()}
                {(isGroupOpen || collapsed) && g.items.map(item => {
                  const active = navActive(item);
                  const isHov = hovered === item.path && !active;
                  const gColors = NAV_GROUP_GRADIENTS[g.group];
                  const tile = NAV_GROUP_PASTEL[g.group] || NAV_GROUP_PASTEL[''];
                  const activeColor = gColors ? gColors[0] : SAP_ACCENT;
                  const iconColor = tile.ink;
                  return (
                    <div key={item.path}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                          window.open(item.path, '_blank');
                        } else {
                          navigate(item.path); if (isMobile) setMobileMenuOpen(false);
                          // Haptic feedback on iOS native app
                          if (isCapacitor) try { window.Capacitor?.Plugins?.Haptics?.impact({ style: 'light' }); } catch(e) {}
                        }
                      }}
                      onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open(item.path, '_blank'); } }}
                      onContextMenu={(e) => { e.preventDefault(); setSidebarCtxMenu({ x: e.clientX, y: e.clientY, path: item.path, label: item.label }); }}
                      onMouseEnter={() => setHovered(item.path)}
                      onMouseLeave={() => setHovered(null)}
                      className="aone-nav-link"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 11,
                        padding: collapsed ? '8px 0' : '9px 10px',
                        margin: collapsed ? '2px 10px' : '2px 10px',
                        borderRadius: 14,
                        cursor: 'pointer',
                        background: active
                          ? '#FFFFFF'
                          : isHov ? 'rgba(255,255,255,0.65)' : 'transparent',
                        border: active ? `1px solid ${tile.ink}1A` : '1px solid transparent',
                        boxShadow: active
                          ? `0 4px 12px ${tile.ink}18, 0 1px 2px rgba(29,29,31,0.06)`
                          : 'none',
                        color: active ? tile.ink : T.sbText,
                        fontWeight: active ? 700 : 500,
                        fontSize: DS.type.base,
                        letterSpacing: '-0.01em',
                        transition: 'all 0.15s cubic-bezier(.34,1.56,.64,1)',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        position: 'relative',
                        transform: isHov ? 'translateX(2px)' : 'translateX(0)',
                      }}
                      title={collapsed ? item.label : undefined}>
                      <span style={{
                        flexShrink: 0,
                        width: 30, height: 30, borderRadius: 9,
                        background: tile.soft,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 1px 2px rgba(29,29,31,0.06), inset 0 1px 0 rgba(255,255,255,0.50)',
                        transform: active ? 'scale(1.05)' : 'scale(1)',
                        transition: 'transform 0.15s cubic-bezier(.34,1.56,.64,1)',
                      }}>
                        {getIcon(item.path, tile.ink)}
                      </span>
                      {!collapsed && (
                        <span style={{ display:'flex', alignItems:'center', gap:5, flex:1, overflow:'hidden', minWidth:0 }}>
                          <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{item.label}</span>
                          {item.areyUnread && areyTotalUnread > 0 && (
                            <span style={{
                              background:'#D14A35', color:'#fff', fontSize:DS.type.xxs, fontWeight:700,
                              padding:'1px 7px', borderRadius:10, flexShrink:0,
                            }}>{areyTotalUnread > 99 ? '99+' : areyTotalUnread}</span>
                          )}
                        </span>
                      )}
                      {collapsed && item.areyUnread && areyTotalUnread > 0 && (
                        <span style={{
                          position:'absolute', top:4, right:4,
                          background:'#D14A35', color:'#fff', fontSize:DS.type.xxs, fontWeight:700,
                          padding:'1px 5px', borderRadius:8, minWidth:14, textAlign:'center',
                        }}>{areyTotalUnread > 9 ? '9+' : areyTotalUnread}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* AERIS — opens already signed in via /open/aeris (same tab).
              Shown to exactly the users who see the Aeris launchpad tile
              (config/ecosystemApps.js); the backend still decides access. */}
          {canSeeAeris(user) && (
            <a href={AERIS_OPEN_PATH}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return; // let the browser open a new tab
                e.preventDefault();
                if (isMobile) setMobileMenuOpen(false);
                navigate(AERIS_OPEN_PATH);
              }}
              onMouseEnter={() => setHovered(AERIS_OPEN_PATH)}
              onMouseLeave={() => setHovered(null)}
              className="aone-nav-link"
              title={collapsed ? 'AERIS — Amazon intelligence (signed in)' : 'Open AERIS, already signed in'}
              style={{
                display: 'flex', alignItems: 'center', gap: 11,
                padding: collapsed ? '8px 0' : '9px 10px',
                margin: '10px 10px 2px',
                borderRadius: 14, cursor: 'pointer', textDecoration: 'none',
                background: hovered === AERIS_OPEN_PATH ? 'rgba(255,255,255,0.65)' : 'transparent',
                border: '1px solid transparent',
                color: T.sbText, fontWeight: 500, fontSize: DS.type.base, letterSpacing: '-0.01em',
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}>
              <span style={{
                flexShrink: 0, width: 30, height: 30, borderRadius: 9, background: '#1A1A2E',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontFamily: "Georgia,'Times New Roman',serif", fontStyle: 'italic', fontSize: 15,
                boxShadow: '0 1px 2px rgba(29,29,31,0.06)',
              }}>a</span>
              {!collapsed && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>AERIS</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, flexShrink: 0 }} aria-hidden="true">
                    <path d="M7 17L17 7"/><path d="M8 7h9v9"/>
                  </svg>
                </span>
              )}
            </a>
          )}
        </div>

        {/* Sidebar profile card — mobile only. On desktop the topbar avatar
            already exposes profile + sign-out, so the bottom card was just
            eating vertical space. */}
        {!collapsed && isMobile && (
          <div style={{ margin:'8px 12px 12px', display:'flex', flexDirection:'column', gap:6 }}>
            <div
              onClick={() => { setShowProfileModal(true); setMobileMenuOpen(false); }}
              style={{
                padding:'12px', borderRadius:16, cursor:'pointer',
                background:'#FFFFFF', border:'1px solid rgba(60,60,67,0.06)',
                display:'flex', alignItems:'center', gap:11,
                boxShadow:'0 4px 14px rgba(29,29,31,0.06), 0 1px 2px rgba(29,29,31,0.04)',
              }}
            >
              <AoneAvatar name={user?.name} seed={user?.email || user?.name} src={profilePhoto} size="sm"
                style={{ border:'2px solid #fff', boxShadow:`0 2px 6px ${SAP_ACCENT_MID}` }} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:DS.type.sm, fontWeight:600, color:T.sbText, letterSpacing:'-0.01em', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.2 }}>{user?.name}</div>
                <div style={{
                  fontFamily:"'Playfair Display', serif", fontStyle:'normal',
                  fontSize:DS.type.xs, fontWeight:600, color:'#5FA86A',
                  textTransform:'capitalize', lineHeight:1.2, marginTop:1,
                }}>{role.replace(/_/g, ' ')}</div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
            </div>
            {/* Mobile-only: Sign out in sidebar */}
            {isMobile && (
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={() => { setMobileMenuOpen(false); logout(); }} style={{
                  flex:1, background: T.sbHover, border: `1px solid ${T.sbBorder}`, borderRadius: 10,
                  color: T.danger, padding: '10px 8px', cursor: 'pointer', fontSize: DS.type.xs, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent:'center', gap: 4,
                }}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* ── Tablet swipe hint — pulsing edge strip (shows once) ── */}
      {isTablet && collapsed && (
        <div style={{
          position:'fixed', left:68, top:'50%', transform:'translateY(-50%)',
          width:3, height:48, borderRadius:3, zIndex:100,
          background:`linear-gradient(180deg, transparent, ${SAP_ACCENT_MID}, transparent)`,
          opacity:0.5, pointerEvents:'none',
          animation:'pulse 3s ease-in-out 3',
        }} />
      )}

      {/* ── Main area ───────────────────────────────────────────────────────── */}
      <div className="aone-main-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, minHeight: 0 }}>

        {/* Fiori shell bar (desktop, opt-in) — replaces sidebar + topbar */}
        {!isMobile && fiori && (
          <FioriShellBar
            user={user}
            unreadCount={unreadCount}
            onBell={() => setNotifOpen(prev => !prev)}
            onSearch={() => setCmdOpen(true)}
            onExit={() => setFiori(false)}
          />
        )}

        {/* Mobile Header */}
        {isMobile && (
          <MobileHeader
            pageTitle={pageTitle}
            T={T}
            unreadCount={unreadCount}
            onNotifToggle={() => setNotifOpen(prev => !prev)}
            notifRef={notifRef}
            profilePhoto={profilePhoto}
            onProfileClick={() => setShowProfileModal(true)}
            role={role}
            onBack={pathname !== '/' && pathname !== '/tasks' ? () => navigate(-1) : undefined}
            onMenu={() => setMenuTrigger(t => t + 1)}
          />
        )}


        {/* Top bar (desktop only) */}
        {!isMobile && !fiori && (
        <div className="aone-topbar" style={{
          height: 52, flexShrink: 0,
          background: `${DS.surface}E0`,
          backdropFilter: 'saturate(180%) blur(28px)',
          WebkitBackdropFilter: 'saturate(180%) blur(28px)',
          borderBottom: `1px solid ${DS.divider}`,
          boxShadow: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px',
          position: 'relative', zIndex: 10,
          gap: 16,
        }}>
          {/* Page title */}
          <div style={{ display:'flex', alignItems:'center', minWidth:0, flex:'0 0 auto', maxWidth: 260 }}>
            <span style={{ fontSize: DS.type.md, fontWeight: 600, color: T.text, letterSpacing: '-0.01em', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {pageTitle}
            </span>
          </div>

          {/* Command Palette Trigger — Spotlight style */}
          <div onClick={() => { setCmdOpen(true); setCmdQuery(''); setCmdResults([]); setCmdSelected(0); }}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 8, height: 32, cursor: 'pointer',
              background: DS.surfaceAlt,
              borderRadius: DS.r.sm, padding: '0 12px',
              transition: 'background 0.15s',
              maxWidth: 480,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = DS.accentSoft; }}
            onMouseLeave={e => { e.currentTarget.style.background = DS.surfaceAlt; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={DS.textMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <span style={{ flex: 1, fontSize: DS.type.sm, color: DS.textMuted, fontFamily: DS.font, userSelect: 'none', fontWeight: 400 }}>Search</span>
            <button
              onClick={(e) => { e.stopPropagation(); setHeaderScanOpen(true); }}
              title="Scan barcode to search" aria-label="Scan barcode to search"
              style={{ display:'flex', alignItems:'center', justifyContent:'center', width:24, height:24, flexShrink:0,
                background:'transparent', border:'none', borderRadius:DS.r.sm, cursor:'pointer', color: DS.textMuted, padding:0 }}
              onMouseEnter={e => { e.currentTarget.style.background = DS.accentSoft; e.currentTarget.style.color = DS.accent; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = DS.textMuted; }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/>
                <line x1="7" y1="12" x2="17" y2="12"/>
              </svg>
            </button>
            <kbd style={{
              fontSize: DS.type.xs, color: DS.textMuted, background: 'transparent',
              fontFamily: DS.font, fontWeight: 400, flexShrink: 0,
            }}>{navigator.platform?.includes('Mac') ? '\u2318' : 'Ctrl'}K</kbd>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

            {/* Notification Bell (desktop) — hidden for security_guard */}
            {role !== 'security_guard' && <div data-notif-toggle="1" onClick={() => setNotifOpen(prev => !prev)} style={{
              position: 'relative', cursor: 'pointer',
              width: 34, height: 34, borderRadius: DS.r.sm,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = DS.accentSoft; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke={DS.textSec} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
              </svg>
              {(unreadCount + areyTotalUnread) > 0 && (
                <div style={{
                  position: 'absolute', top: 6, right: 6,
                  width: 8, height: 8, borderRadius: '50%',
                  background: T.danger,
                  border: `1.5px solid ${DS.surface}`,
                }} />
              )}
            </div>}

            {/* Profile avatar — circular, 30pt, opens a dropdown with Profile + Sign out */}
            <div style={{ position: 'relative' }}>
              <div
                onClick={(e) => { e.stopPropagation(); setAvatarMenuOpen(o => !o); }}
                style={{ cursor: 'pointer', flexShrink: 0 }}
                title={user?.name}
              >
                <AoneAvatar name={user?.name} seed={user?.email || user?.name} src={profilePhoto} size="sm"
                  style={{ boxShadow: avatarMenuOpen ? `0 0 0 2px ${SAP_ACCENT_MID}` : 'inset 0 1px 0 rgba(255,255,255,0.35), 0 2px 6px rgba(0,0,0,0.10)', transition: 'box-shadow 0.15s' }} />
              </div>
              {avatarMenuOpen && (
                <>
                  <div onClick={() => setAvatarMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                    background: DS.surface, border: `1px solid ${DS.border}`,
                    borderRadius: DS.r.md, padding: 6, minWidth: 220, zIndex: 999,
                    boxShadow: DS.shadow.xl,
                  }}>
                    <div style={{ padding: '10px 12px 8px', borderBottom: `1px solid ${DS.divider}`, marginBottom: 4 }}>
                      <div style={{ fontSize: DS.type.sm, fontWeight: 700, color: T.text }}>{user?.name || 'Signed in'}</div>
                      <div style={{ fontSize: DS.type.xs, color: DS.textMuted, marginTop: 2 }}>{user?.email}</div>
                    </div>
                    <div onClick={() => { setAvatarMenuOpen(false); setShowProfileModal(true); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', fontSize: DS.type.sm, color: T.text, cursor: 'pointer', borderRadius: DS.r.sm, fontWeight: 500 }}
                      onMouseEnter={e => e.currentTarget.style.background = T.accentSoft}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      Profile & Settings
                    </div>
                    <div onClick={() => { setAvatarMenuOpen(false); navigate('/two-factor'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', fontSize: DS.type.sm, color: T.text, cursor: 'pointer', borderRadius: DS.r.sm, fontWeight: 500 }}
                      onMouseEnter={e => e.currentTarget.style.background = T.accentSoft}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      Two-Factor Auth
                    </div>
                    <div onClick={() => { setAvatarMenuOpen(false); navigate('/'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', fontSize: DS.type.sm, color: T.text, cursor: 'pointer', borderRadius: DS.r.sm, fontWeight: 500 }}
                      onMouseEnter={e => e.currentTarget.style.background = T.accentSoft}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                      Dashboard
                    </div>
                    {user?.role !== 'founder' && (
                      <div onClick={() => { setAvatarMenuOpen(false); leaveCompany(); }}
                        title="Locks your account immediately. Only a founder can reopen it."
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', fontSize: DS.type.sm, color: T.textSec, cursor: 'pointer', borderRadius: DS.r.sm }}
                        onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        Hand in my access
                      </div>
                    )}
                    <div style={{ height: 1, background: DS.divider, margin: '4px 0' }} />
                    {/* Sign out used to fade out at the moment you pointed at
                        it — the old red on a hardcoded #FEE9E7 hover measured
                        4.14:1 while the items above sat at 14:1, so the row
                        read as disabled. Fixed at the token: danger is now
                        text-safe on its own tint (6.80:1). */}
                    <div onClick={() => { setAvatarMenuOpen(false); logout(); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', fontSize: DS.type.sm, color: T.danger, cursor: 'pointer', borderRadius: DS.r.sm, fontWeight: 600 }}
                      onMouseEnter={e => e.currentTarget.style.background = T.dangerBg}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      Sign out
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Content — SAP Fiori mobile: 48px header + 56px bottom nav */}
        <PhoneCopyEnhancer />
        <BmrPreflightHost />
        <div id="aone-content" ref={contentRef} className={isMobile ? 'aone-mobile-scroll' : undefined} style={{ flex: 1, minHeight: 0, background: T.content, backgroundImage: 'var(--aone-content-wash), ' + T.contentGradient, position: 'relative', WebkitOverflowScrolling: 'touch', ...(isFullHeightPage ? { overflow: 'hidden', display: 'flex', flexDirection: 'column', ...(isMobile ? { paddingTop: 'calc(48px + env(safe-area-inset-top, 0px))', paddingBottom: 'calc(78px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box' } : isTablet ? { paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)', paddingBottom: 'calc(78px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box' } : {}) } : { overflowY: 'auto', overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column', ...(isMobile ? { paddingTop: 'calc(48px + env(safe-area-inset-top, 0px))', overflowX: 'hidden', boxSizing: 'border-box' } : isTablet ? { paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)', paddingBottom: 'calc(78px + env(safe-area-inset-bottom, 0px))', overflowX: 'hidden', boxSizing: 'border-box' } : {}) }) }}>
          {/* Pull-to-refresh indicator */}
          {(pullDistance > 0 || isRefreshing) && (
            <div style={{
              position:'sticky', top:0, zIndex:50, display:'flex', justifyContent:'center', alignItems:'center',
              height: pullDistance, overflow:'hidden', transition: isPulling.current ? 'none' : 'height 0.3s ease',
              background:`linear-gradient(180deg, ${SAP_ACCENT_SOFT}, transparent)`,
            }}>
              <div style={{
                width:32, height:32, borderRadius:'50%',
                border:`3px solid ${SAP_ACCENT_MID}`, borderTopColor: SAP_ACCENT,
                animation: isRefreshing ? 'spin 0.8s linear infinite' : 'none',
                transform: !isRefreshing ? `rotate(${pullDistance * 3}deg)` : 'none',
                transition: 'transform 0.1s',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          )}
          {(() => {
            // Route-level access guard — blocks direct URL navigation to restricted modules.
            // Nav filtering in visibleNav only hides menu items; this is the hard enforcement.
            const ALWAYS_ALLOW = ['/', '/settings', '/tasks', '/salary', '/attendance', '/calendar', '/reminders', '/client360', '/sku360', '/vendor360', '/documents', '/pulse-documents'];
            // /360 hub enforces access itself (AnyModuleGuard route + per-tab
            // filter inside the page) — the flat canAccess check can't express
            // "any of four modules", so let it through here.
            const is360Hub = pathname === '/360' || pathname.startsWith('/360/');
            // Same deal for the creator hub (Influencer Hub + Affiliate + Rewards).
            const isLoveSquadHub = pathname === '/lovesquad-hub' || pathname.startsWith('/lovesquad-hub/');
            // Growth Command Centre: AnyModuleGuard on the route (influencers OR marketing).
            const isGrowthHub = pathname === '/growth' || pathname.startsWith('/growth/');
            if (!is360Hub && !isLoveSquadHub && !isGrowthHub && !ALWAYS_ALLOW.includes(pathname) && !canAccess(pathname)) {
              return (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:16, userSelect:'none' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={SAP_ACCENT} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  <div style={{ fontSize:DS.type.xl, fontWeight:700, color:T.text }}>Access Denied</div>
                  <div style={{ fontSize:DS.type.sm, color:T.textMuted, textAlign:'center', maxWidth:340, lineHeight:1.6 }}>
                    You don't have permission to view this module.<br />Contact your administrator to request access.
                  </div>
                  <button
                    onClick={() => navigate('/')}
                    style={{ marginTop:4, padding:'10px 28px', borderRadius:10, border:'none', background:SAP_ACCENT, color:'#fff', fontWeight:600, fontSize:DS.type.sm, cursor:'pointer' }}
                  >
                    Go to Dashboard
                  </button>
                </div>
              );
            }
            // One wrapper, same shape at every width. Mobile used to wrap the
            // page in its own keyed div while desktop rendered it bare, so
            // crossing 768px (browser zoom, a resize, a pinch) unmounted the
            // whole page: lightbox gone, every list refetched (2026-09-12).
            // The content column is a flex column at every width now, so the
            // page grows to fill it and the footer band sits at the bottom of
            // the viewport on short pages instead of crawling up under the
            // last card (2026-09-15). minWidth:0 + width:100% because a flex
            // item's minimum width is its content: without them a wide table
            // (Sales Orders) pushed the whole page out sideways.
            return (
              <ModuleThemeProvider>
                <div className={isMobile && !isFullHeightPage ? 'aone-mpage' : undefined}
                  style={isFullHeightPage ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { flex: '1 0 auto', minWidth: 0, width: '100%', maxWidth: '100%', paddingBottom: isMobile ? 0 : 24 }}>
                  {children}
                </div>
              </ModuleThemeProvider>
            );
          })()}
          {/* Footer — hidden on full-height pages like Arey, Instagram Inbox, Aira */}
          {/* marginTop:auto pins the band to the true bottom of the flex column
              on short mobile pages instead of floating mid-screen. */}
          {/* On mobile the band carries the tab-bar clearance so the bar can
              float over it with no gap under its rounded corners. The wine
              behind it was right while the bar was dark smoked glass; once the
              bar became paper the same band read as two overlapping surfaces,
              so on mobile it is the page ground and the bar sits on it. The
              desktop footer, which has no bar over it, keeps the wine. */}
          {!isFullHeightPage && <div style={{ padding: isMobile ? '16px 0 calc(96px + env(safe-area-inset-bottom, 0px))' : '16px 0', textAlign:'center', marginTop: 'auto', position:'relative', background: isMobile ? DS.bg : SAP_ACCENT }}>
            <span style={{ fontSize:DS.type.xxs, color: isMobile ? DS.textMuted : '#FFFFFF', letterSpacing:'0.08em', fontWeight:500, textTransform:'uppercase' }}>
              Powered by {APP_NAME}
            </span>
          </div>}
        </div>

        {/* Floating Jarvis orb and Arey dock removed — Arey stays reachable from
            the sidebar nav (desktop) and the bottom tab bar (mobile); the two
            floating circles cluttered every module on both viewports. */}


        {/* Offline banner — last-known data keeps rendering underneath */}
        <OfflineBanner />

        {/* Deploy reload banner */}
        {reloadCountdown !== null && (
          <div style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            background: '#1a1a2e', color: '#fff', borderRadius: 12, padding: '12px 20px',
            fontFamily: DS.font, fontSize: DS.type.sm, fontWeight: 500,
            boxShadow: '0 4px 24px rgba(0,0,0,0.35)', zIndex: 999999,
            display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap',
          }}>
            <span>🚀 New version deployed — refreshing in {reloadCountdown}s</span>
            <button onClick={() => window.location.reload()} style={{
              background: '#fff', color: '#0F1017', border: 'none', borderRadius: 6,
              padding: '5px 12px', fontSize: DS.type.xs, fontWeight: 700, cursor: 'pointer',
            }}>Refresh now</button>
          </div>
        )}

        {/* Mobile Bottom Nav — phones AND tablets (iPad gets the Capsule too;
            the sidebar stays available there for the desktop workspaces). */}
        {(isMobile || isTablet) && (
          <MobileBottomNav
            T={T}
            unreadCount={unreadCount}
            areyUnread={areyTotalUnread}
            onNotifToggle={() => setNotifOpen(prev => !prev)}
            onProfileClick={() => setShowProfileModal(true)}
            profilePhoto={profilePhoto}
            onScanSearch={(v) => navigate(`/skus?q=${encodeURIComponent(v)}`)}
            openTrigger={menuTrigger}
          />
        )}

        {/* Notification Panel — renders when bell is toggled.
            Always rendered with light background + dark text for guaranteed
            contrast (the theme-driven T.text on dark theme is a dark navy,
            which produced unreadable dark-on-dark text). */}
        {notifOpen && (() => {
          const NP = {
            bg: '#FFFFFF',
            border: 'rgba(0,0,0,0.10)',
            text: T.text,
            textMuted: T.textSec,
            hover: 'rgba(0,0,0,0.05)',
            accent: SAP_ACCENT,
            accentLight: SAP_ACCENT_SOFT,
          };
          return (
          <div ref={notifPanelRef} style={{
            position: 'fixed',
            top: isMobile ? 60 : 64,
            right: isMobile ? 8 : 24,
            left: isMobile ? 8 : 'auto',
            width: isMobile ? 'auto' : 380,
            maxHeight: isMobile ? 'calc(100vh - 140px)' : 'calc(100vh - 96px)',
            zIndex: 1200,
            background: NP.bg,
            border: `1px solid ${NP.border}`,
            borderRadius: 16,
            boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: `1px solid ${NP.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: DS.type.md, fontWeight: 700, color: NP.text }}>Notifications</span>
                {unreadCount > 0 && (
                  <span style={{
                    fontSize: DS.type.xxs, fontWeight: 700, color: '#fff',
                    background: T.danger, padding: '2px 7px', borderRadius: 10,
                  }}>{unreadCount}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    fontSize: DS.type.xs, fontWeight: 600, color: NP.accent, padding: '4px 8px', borderRadius: 6,
                  }} title="Mark all as read">Mark all read</button>
                )}
                {notifications.length > 0 && (
                  <button onClick={clearAllNotifications} style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    fontSize: DS.type.xs, fontWeight: 600, color: NP.textMuted, padding: '4px 8px', borderRadius: 6,
                  }} title="Clear all">Clear</button>
                )}
                <button onClick={() => setNotifOpen(false)} style={{
                  background: NP.hover, border: 'none', borderRadius: 8, cursor: 'pointer',
                  width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} title="Close">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={NP.textMuted} strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {notifications.length === 0 ? (
                <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%', background: NP.hover,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={NP.textMuted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: DS.type.sm, fontWeight: 600, color: NP.text, marginBottom: 4 }}>You're all caught up</div>
                  <div style={{ fontSize: DS.type.xs, color: NP.textMuted }}>No new notifications</div>
                </div>
              ) : notifications.map((n, i) => {
                const isUnread = !n.readAt;
                return (
                  <div key={n.id || n._id || i} onClick={() => markNotifRead(n)} style={{
                    padding: '12px 16px',
                    borderBottom: i < notifications.length - 1 ? `1px solid ${NP.border}` : 'none',
                    background: isUnread ? NP.accentLight : 'transparent',
                    cursor: 'pointer', display: 'flex', gap: 10,
                    transition: 'background 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = NP.hover; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isUnread ? NP.accentLight : 'transparent'; }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                      background: 'rgba(0,0,0,0.04)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {notifIcon(n.type)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: DS.type.sm, fontWeight: isUnread ? 700 : 500, color: NP.text,
                        marginBottom: 2, lineHeight: 1.35,
                      }}>{n.title || n.message || 'Notification'}</div>
                      {n.title && n.message && n.message !== n.title && (
                        <div style={{ fontSize: DS.type.xs, color: NP.textMuted, lineHeight: 1.4, marginBottom: 4 }}>
                          {n.message}
                        </div>
                      )}
                      <div style={{ fontSize: DS.type.xxs, color: NP.textMuted, fontWeight: 500 }}>
                        {timeAgo(n.createdAt)}
                      </div>
                    </div>
                    {isUnread && (
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%', background: NP.accent,
                        flexShrink: 0, marginTop: 6,
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          );
        })()}
      </div>
    </div>
      {/* ── Profile Photo Modal — iPhone Contact Card Style ───────────────── */}
      {showProfileModal && (
        <div style={{
          position:'fixed', inset:0, zIndex:9999,
          background:'rgba(0,0,0,0.7)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }} onClick={() => setShowProfileModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            width:380, maxWidth:'90vw', borderRadius:28, overflow:'hidden',
            background: '#fff',
            boxShadow:'0 32px 80px rgba(0,0,0,0.5)',
            border:`1px solid ${T.border}`,
          }}>
            {/* Photo area — full width, tall, like iPhone contact card */}
            <div style={{
              width:'100%', height:320, position:'relative', overflow:'hidden',
              background: profilePhoto ? 'transparent' : `linear-gradient(160deg, ${SAP_ACCENT}, #7C5A3C, ${SAP_ACCENT_HOVER})`,
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              {profilePhoto ? (
                <img src={fileSrc(profilePhoto, fileToken)} alt={user?.name} style={{
                  width:'100%', height:'100%', objectFit:'cover', display:'block',
                }} />
              ) : (
                <div style={{
                  fontSize:120, fontWeight:300, color:'rgba(255,255,255,0.4)',
                  fontFamily:"'Playfair Display', Georgia, serif",
                }}>{user?.name?.[0]?.toUpperCase()}</div>
              )}

              {/* Gradient overlay at bottom for name */}
              <div style={{
                position:'absolute', bottom:0, left:0, right:0, height:140,
                background:'linear-gradient(transparent, rgba(0,0,0,0.7))',
              }} />

              {/* Name overlay */}
              <div style={{
                position:'absolute', bottom:20, left:24, right:24,
              }}>
                <div style={{
                  fontSize:DS.type.xxxl, fontWeight:800, color:'#fff', letterSpacing:'-0.02em',
                  textShadow:'0 2px 8px rgba(0,0,0,0.4)',
                }}>{user?.name}</div>
                <div style={{
                  fontSize:DS.type.sm, color:'rgba(255,255,255,0.7)', fontWeight:500, marginTop:2,
                  textTransform:'capitalize',
                }}>{role.replace(/_/g, ' ')}</div>
              </div>

              {/* Close button */}
              <button onClick={() => setShowProfileModal(false)} style={{
                position:'absolute', top:14, right:14, width:32, height:32, borderRadius:16,
                background:'rgba(0,0,0,0.4)', border:'none', color:'#fff', fontSize:DS.type.base,
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                backdropFilter:'blur(8px)',
              }}>{'\u2715'}</button>

              {/* Camera button — upload trigger */}
              <button onClick={() => profileFileRef.current?.click()} disabled={profileUploading} style={{
                position:'absolute', bottom:16, right:16, width:40, height:40, borderRadius:20,
                background:'rgba(255,255,255,0.2)', border:'2px solid rgba(255,255,255,0.5)',
                color:'#fff', fontSize:DS.type.lg, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center',
                backdropFilter:'blur(8px)', transition:'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                title="Change photo"
              >
                {profileUploading ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" style={{ animation:'spin 1s linear infinite' }}><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" fill="none"/><path d="M12 2a10 10 0 019.8 8" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                )}
              </button>
              <input ref={profileFileRef} type="file" accept="image/*" onChange={handleProfilePhotoUpload} style={{ display:'none' }} />
            </div>

            {/* Action buttons — like iPhone contact card */}
            <div style={{ padding:'16px 20px', display:'flex', gap:10, justifyContent:'center' }}>
              <button onClick={() => profileFileRef.current?.click()} style={{
                flex:1, padding:'12px 16px', borderRadius:14,
                background: 'rgba(0,0,0,0.04)',
                border:'none', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4,
                transition:'all 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.07)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={SAP_ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                <span style={{ fontSize:DS.type.xs, fontWeight:600, color:SAP_ACCENT }}>
                  {profilePhoto ? 'Change Photo' : 'Add Photo'}
                </span>
              </button>

              {profilePhoto && (
                <button onClick={removeProfilePhoto} style={{
                  flex:1, padding:'12px 16px', borderRadius:14,
                  background: 'rgba(0,0,0,0.04)',
                  border:'none', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4,
                  transition:'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                  </svg>
                  <span style={{ fontSize:DS.type.xs, fontWeight:600, color:T.danger }}>Remove</span>
                </button>
              )}

            </div>

            {/* Tip text */}
            <div style={{ padding:'0 20px 18px', textAlign:'center' }}>
              <p style={{ fontSize:DS.type.xs, color:T.textMuted, margin:0, lineHeight:1.5 }}>
                Your photo helps teammates recognize you across offices. Update it anytime!
              </p>
            </div>
          </div>
        </div>
      )}
      {/* ── Command Palette Overlay ──────────────────────────────── */}
      {headerScanOpen && createPortal(
        <BarcodeScanner
          title="Scan to search"
          subtitle="Scan any barcode — white-on-black labels supported"
          onDetected={(text) => { setHeaderScanOpen(false); navigate(`/skus?q=${encodeURIComponent(text)}`); }}
          onClose={() => setHeaderScanOpen(false)}
        />, document.body)}
      {cmdOpen && createPortal(
        <div style={{ position:'fixed', inset:0, zIndex:999999, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:'12vh' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setCmdOpen(false); setCmdQuery(''); } }}>
          {/* Backdrop */}
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)' }} />
          {/* Palette — PT tokens are palette-surface-aware (dark bg needs light text) */}
          {(() => {
            const isDark = false; // app is light-only by design
            const PT = {
              bg:       isDark ? '#16171F' : '#FFFFFF',
              text:     isDark ? '#E8EAF0' : '#0D0D0D',
              textSec:  isDark ? '#B0B8CE' : '#3A3A3A',
              muted:    isDark ? '#7B8299' : '#888888',
              border:   isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
              iconBg:   isDark ? 'rgba(255,255,255,0.07)' : '#F4F4F6',
              selBg:    isDark ? 'rgba(177,162,216,0.18)' : 'rgba(177,162,216,0.10)',
            };
            return (
          <div style={{
            position:'relative', width:'min(560px, 92vw)', maxHeight:'60vh', background: PT.bg,
            borderRadius:18, boxShadow: isDark ? '0 25px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)' : '0 25px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
            border:'none',
            display:'flex', flexDirection:'column', animation:'slideUp 0.15s ease', overflow:'hidden',
          }}>
            {/* Search input */}
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 18px', borderBottom:`1px solid ${PT.border}` }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={PT.muted} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input ref={cmdInputRef} value={cmdQuery} onChange={e=>handleCmdInput(e.target.value)}
                placeholder="Search or jump to..." autoComplete="off"
                onKeyDown={e => { if (e.key === 'Enter' && isAiQuery(cmdQuery) && !aiMode) { e.preventDefault(); askAI(); } }}
                style={{ flex:1, border:'none', outline:'none', background:'transparent', color:PT.text, fontSize:DS.type.md, fontFamily:DS.font, fontWeight:500 }} />
              {cmdLoading && <svg width="18" height="18" viewBox="0 0 24 24" style={{animation:'spin 1s linear infinite',flexShrink:0}}><circle cx="12" cy="12" r="10" fill="none" stroke={PT.muted} strokeWidth="2" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>}
              {isAiQuery(cmdQuery) && !aiMode && !aiLoading && (
                <button onClick={askAI} style={{
                  display:'flex', alignItems:'center', gap:4, padding:'4px 10px',
                  background:`linear-gradient(135deg, ${SAP_ACCENT}, #C49A6C)`,
                  border:'none', borderRadius:8, cursor:'pointer', color:'#fff',
                  fontSize:DS.type.xs, fontWeight:600, whiteSpace:'nowrap', flexShrink:0,
                }}>
                  ✦ Ask AI
                </button>
              )}
              {aiLoading && <svg width="18" height="18" viewBox="0 0 24 24" style={{animation:'spin 1s linear infinite',flexShrink:0}}><circle cx="12" cy="12" r="10" fill="none" stroke={SAP_ACCENT} strokeWidth="2" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>}
              <button onClick={() => setCmdScanOpen(true)} title="Scan barcode to search" aria-label="Scan barcode to search"
                style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, flexShrink:0,
                  background:PT.iconBg, border:'none', borderRadius:8, cursor:'pointer', color:PT.textSec, fontSize:DS.type.md }}>
                📷
              </button>
              <kbd style={{ fontSize:DS.type.xxs, color:PT.muted, background:isDark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)', padding:'3px 7px', borderRadius:5, fontFamily:DS.font, border:`1px solid ${PT.border}` }}>ESC</kbd>
            </div>
            {cmdScanOpen && (
              <BarcodeScanner
                title="Scan to search"
                subtitle="Scan any barcode — white-on-black labels supported"
                onDetected={(text) => { setCmdScanOpen(false); setCmdOpen(false); navigate(`/skus?q=${encodeURIComponent(text)}`); }}
                onClose={() => setCmdScanOpen(false)}
              />
            )}
            {/* Results */}
            <div style={{ flex:1, overflowY:'auto', padding: aiMode ? 0 : '6px 0' }}>
              {aiMode ? (
                <div style={{ padding:'16px 18px' }}>
                  {/* Tools used pills */}
                  {aiToolsUsed.length > 0 && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
                      {aiToolsUsed.map(t => (
                        <span key={t} style={{ fontSize:DS.type.xxs, padding:'2px 8px', borderRadius:20,
                          background: isDark?'rgba(177,162,216,0.15)':'rgba(177,162,216,0.1)',
                          color: isDark?T.infoBg:T.info, fontWeight:600 }}>
                          {t.replace(/_/g,' ')}
                        </span>
                      ))}
                    </div>
                  )}
                  {aiLoading ? (
                    <div style={{ display:'flex', alignItems:'center', gap:10, color:PT.muted, fontSize:DS.type.sm }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" style={{animation:'spin 1s linear infinite',flexShrink:0}}>
                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="31.4 31.4" strokeLinecap="round"/>
                      </svg>
                      Thinking...
                    </div>
                  ) : (
                    <div style={{ fontSize:DS.type.sm, lineHeight:1.7, color:PT.text, whiteSpace:'pre-wrap' }}>
                      {aiAnswer}
                    </div>
                  )}
                  {/* Back to search */}
                  {!aiLoading && (
                    <button onClick={() => { setAiMode(false); setAiAnswer(''); setTimeout(()=>cmdInputRef.current?.focus(),50); }}
                      style={{ marginTop:12, fontSize:DS.type.xs, color:PT.muted, background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline' }}>
                      ← Back to search results
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {cmdFiltered.length === 0 && cmdQuery.length >= 2 && (
                    <div style={{ padding:'24px', textAlign:'center', color:PT.muted, fontSize:DS.type.sm }}>No results for "{cmdQuery}"</div>
                  )}
                  {cmdFiltered.map((item, i) => (
                    <div key={`${item.type}-${item.label}-${i}`}
                      onClick={() => executeCmdItem(item)}
                      style={{
                        display:'flex', alignItems:'center', gap:10, padding:'10px 18px', cursor:'pointer',
                        background: i === cmdSelected ? PT.selBg : 'transparent',
                        transition:'background 0.08s',
                      }}
                      onMouseEnter={() => setCmdSelected(i)}>
                      <span style={{ width:28, height:28, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:DS.type.base,
                        background: item.type==='action' ? (isDark?'rgba(177,162,216,0.2)':'rgba(177,162,216,0.10)') : item.type==='result' ? (isDark?'rgba(16,185,129,0.15)':T.successBg) : item.type==='fav' ? (isDark?'rgba(245,158,11,0.15)':T.warningBg) : PT.iconBg,
                        color: item.type==='action' ? (isDark?T.infoBg:T.info) : item.type==='result' ? '#10B981' : item.type==='fav' ? T.warning : PT.textSec,
                        flexShrink:0, fontWeight:700,
                      }}>{item.icon || '→'}</span>
                      <div style={{ flex:1, overflow:'hidden' }}>
                        <div style={{ fontSize:DS.type.base, fontWeight:600, color:PT.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.label}</div>
                        {(item.subtitle || item.group) && <div style={{ fontSize:DS.type.xs, color:PT.muted, marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.subtitle || item.group}</div>}
                      </div>
                      {item.type === 'nav' && <span style={{ fontSize:DS.type.xxs, color:PT.muted, fontFamily:'monospace' }}>{item.path}</span>}
                      {i === cmdSelected && <span style={{ fontSize:DS.type.xxs, color:PT.muted }}>↵</span>}
                    </div>
                  ))}
                </>
              )}
            </div>
            {/* Footer */}
            <div style={{ padding:'8px 18px', borderTop:`1px solid ${PT.border}`, display:'flex', gap:16, alignItems:'center' }}>
              {isAiQuery(cmdQuery) && !aiMode ? (
                <>
                  <span style={{ fontSize:DS.type.xxs, color:T.accent, fontWeight:600 }}>✦ Click "Ask AI" for intelligent answer</span>
                  <span style={{ fontSize:DS.type.xxs, color:PT.muted, marginLeft:'auto' }}>ESC Close</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize:DS.type.xxs, color:PT.muted }}>↑↓ Navigate</span>
                  <span style={{ fontSize:DS.type.xxs, color:PT.muted }}>↵ Select</span>
                  <span style={{ fontSize:DS.type.xxs, color:PT.muted }}>ESC Close</span>
                </>
              )}
            </div>
          </div>
            );
          })()}
        </div>,
        document.body
      )}

      {/* ── Sidebar right-click context menu ─────────────────────────────── */}
      {sidebarCtxMenu && (() => {
        // Resolve menu colors explicitly per-theme. Previously the items used
        // `color: T.text` which in dark mode is #3A4258 (dark navy) sitting on
        // a #1E1E2E background — the text was almost invisible. We now use
        // ivory-cream surface in light mode (matches AONE Stratus) and a
        // raised neutral in dark mode, with a guaranteed-contrasting text
        // colour for each.
        const menuBg     = '#FFFFFF';
        const menuText   = '#0D0D0D';
        const menuMuted  = '#7A5448';
        const menuHover  = SAP_ACCENT_SOFT;
        const menuBorder = 'rgba(0,0,0,0.10)';
        return createPortal(
          <div style={{
            position: 'fixed', top: sidebarCtxMenu.y, left: Math.min(sidebarCtxMenu.x, window.innerWidth - 200),
            background: menuBg,
            border: `1px solid ${menuBorder}`,
            borderRadius: 10, padding: 5, zIndex: 10000, minWidth: 180,
            backdropFilter: 'blur(10px) saturate(1.2)', WebkitBackdropFilter: 'blur(10px) saturate(1.2)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.13), 0 0 0 1px rgba(0,0,0,0.05)',
            fontFamily: DS.font,
          }} onClick={e => e.stopPropagation()}>
            <div
              onClick={() => { window.open(sidebarCtxMenu.path, '_blank'); setSidebarCtxMenu(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px',
                borderRadius: 7, cursor: 'pointer', fontSize: DS.type.sm, fontWeight: 500,
                color: menuText, transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = menuHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={menuMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              Open in new tab
            </div>
            <div
              onClick={() => {
                navigator.clipboard.writeText(window.location.origin + sidebarCtxMenu.path);
                setSidebarCtxMenu(null);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px',
                borderRadius: 7, cursor: 'pointer', fontSize: DS.type.sm, fontWeight: 500,
                color: menuText, transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = menuHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={menuMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
              </svg>
              Copy link
            </div>
          </div>,
          document.body
        );
      })()}

    </ModuleThemeProvider>
    </ThemeContext.Provider>
  );
}
